import React, { useState, useEffect, useContext, useRef } from "react";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { useNavigate, useParams } from "react-router-dom";
import { serverHost } from "variables/serverdata";
import {
  Button, Card, CardHeader, CardBody, FormGroup, Form,
  Input, Container, Row, Col, Alert, Table, Spinner, Modal,
  ModalHeader, ModalBody, ModalFooter
} from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader.js";
import {
  getUser, getSite, validateBackgroundFile,
  getDeviceCallLogs, subscribeToDeviceEvents, pullDeviceEvents,
} from "./deviceService";
import HusariaSelect from "components/HusariaSelect";

const DIRECTORY_IMAGE_COLOR_OPTIONS = [
  { label: "Red", hex: "#ef4444" },
  { label: "Orange", hex: "#f97316" },
  { label: "Yellow", hex: "#facc15" },
  { label: "Green", hex: "#22c55e" },
  { label: "Blue", hex: "#3b82f6" },
  { label: "Indigo", hex: "#6366f1" },
  { label: "Violet", hex: "#a855f7" },
  { label: "White", hex: "#ffffff" },
  { label: "Gray", hex: "#6b7280" },
  { label: "Black", hex: "#000000" },
];

const DIRECTORY_IMAGE_SIZE = 432;
const AKUVOX_S539_MODEL = "Akuvox S539";
const CURRENT_2N_DEVICE_MODELS = new Set([
  "2N IP Style",
  "2N IP Verso",
  "2N IP Solo",
]);
const ACTIVITY_DEVICE_MODELS = new Set([
  ...CURRENT_2N_DEVICE_MODELS,
  AKUVOX_S539_MODEL,
]);

const isValidHexColor = (hexColor) => {
  return /^#[0-9a-fA-F]{6}$/.test(String(hexColor || "").trim());
};

const createSolidColorDirectoryImageDataUrl = (hexColor) => {
  return new Promise((resolve, reject) => {
    if (!isValidHexColor(hexColor)) {
      reject(new Error("Invalid directory image color"));
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = DIRECTORY_IMAGE_SIZE;
    canvas.height = DIRECTORY_IMAGE_SIZE;

    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("Could not create directory color image"));
      return;
    }

    ctx.fillStyle = hexColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    resolve(canvas.toDataURL("image/png"));
  });
};

function DeviceForm() {
  const navigate = useNavigate();
  const { device_uuid } = useParams();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const editing = Boolean(device_uuid);
  const [directories, setDirectories] = useState([]);
  const [directoryTenants, setDirectoryTenants] = useState([]);
  const [saving, setSaving] = useState(false);
  const [backgroundFile, setBackgroundFile] = useState(null);
  const [backgroundPreview, setBackgroundPreview] = useState(null);
  const [savedBackgroundPreview, setSavedBackgroundPreview] = useState(null);
  const [backgroundUploading, setBackgroundUploading] = useState(false);
  const [backgroundStatus, setBackgroundStatus] = useState({
    show: false,
    success: false,
    message: "",
  });
  const [backgroundMeta, setBackgroundMeta] = useState({
    width: null,
    height: null,
    validDimensions: false,
  });
  const [backgroundRemoving, setBackgroundRemoving] = useState(false);
  const [extensionOptions, setExtensionOptions] = useState([]);
  const [quickSetupBusy, setQuickSetupBusy] = useState(false);
  const [quickSetupStatus, setQuickSetupStatus] = useState({
    show: false,
    success: false,
    message: "",
  });
  const [rfidBacklightEnabled, setRfidBacklightEnabled] = useState(false);
  const [rfidBacklightLoading, setRfidBacklightLoading] = useState(false);
  const [initialRfidBacklightEnabled, setInitialRfidBacklightEnabled] = useState(false);
  const [showcaseMode, setShowcaseMode] = useState("OFF");
  const [showcaseModeLoading, setShowcaseModeLoading] = useState(false);
  const [initialShowcaseMode, setInitialShowcaseMode] = useState("OFF");
  const [rootFolderDisplay, setRootFolderDisplay] = useState("CARDS_1_COLUMN");
  const [rootFolderDisplayLoading, setRootFolderDisplayLoading] = useState(false);
  const [initialRootFolderDisplay, setInitialRootFolderDisplay] = useState("CARDS_1_COLUMN");

  const defaultStyleDisplaySettings = {
    codeEntryButton: true,
    entryKeypadMode: "NORMAL",
    touchSounds: true,
    flexiBuilderEnabled: false,
  };

  const [styleDisplaySettings, setStyleDisplaySettings] = useState(defaultStyleDisplaySettings);
  const [initialStyleDisplaySettings, setInitialStyleDisplaySettings] = useState(defaultStyleDisplaySettings);
  const [styleDisplaySettingsLoading, setStyleDisplaySettingsLoading] = useState(false);

  const defaultVersoDisplaySettings = {
    phonebookDisplayed: true,
    codeEntryButton: true,
    entryKeypadMode: "NORMAL",
    preferIconsToText: false,
  };

  const [versoDisplaySettings, setVersoDisplaySettings] = useState(defaultVersoDisplaySettings);
  const [initialVersoDisplaySettings, setInitialVersoDisplaySettings] = useState(defaultVersoDisplaySettings);
  const [versoDisplaySettingsLoading, setVersoDisplaySettingsLoading] = useState(false);

  const [directoryDisplaySettings, setDirectoryDisplaySettings] = useState({});
  const [initialDirectoryDisplaySettings, setInitialDirectoryDisplaySettings] = useState({});
  const isBusy = saving || backgroundUploading || backgroundRemoving || quickSetupBusy;

  const [callLogs, setCallLogs] = useState([]);
  const [callLogsLoading, setCallLogsLoading] = useState(false);
  const [callLogsError, setCallLogsError] = useState("");

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState("");

  const [callLogsOpen, setCallLogsOpen] = useState(false);
  const [liveEventsOpen, setLiveEventsOpen] = useState(false);
  const [displaySettingsOpen, setDisplaySettingsOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);

  const [eventFilter, setEventFilter] = useState("ALL");
  const [callLogsRange, setCallLogsRange] = useState("7D");
  const [eventsRange, setEventsRange] = useState("1D");
  const [eventsRequestId, setEventsRequestId] = useState(0);

  const MAX_DIRECTORY_IMAGE_BYTES = 93388; // 98304 is 2n max size, but using lower value as safer threshold

  const MAX_QUICK_DIAL_BUTTON_COUNT = 100;
  const MAX_TENANTS_PER_DEVICE_BUTTON = 16;

  const TENANT_PICKER_PAGE_SIZE = 5;

  const [tenantPickerOpen, setTenantPickerOpen] = useState(false);
  const [tenantPickerButtonNumber, setTenantPickerButtonNumber] = useState(null);
  const [tenantPickerSearch, setTenantPickerSearch] = useState("");
  const [tenantPickerSelected, setTenantPickerSelected] = useState([]);
  const [tenantPickerPage, setTenantPickerPage] = useState(1);

  const [imageEditorOpen, setImageEditorOpen] = useState(false);
  const [imageEditorDirUuid, setImageEditorDirUuid] = useState(null);
  const [imageEditorSrc, setImageEditorSrc] = useState(null);
  const [imageEditorFileName, setImageEditorFileName] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1.0);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [imageEditorError, setImageEditorError] = useState("");
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, panX: 0, panY: 0 });
  const imageEditorRef = useRef(null);
  const [imageEditorResultTooLarge, setImageEditorResultTooLarge] = useState(false);
  const [imageEditorResultBytes, setImageEditorResultBytes] = useState(0);

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: isDark ? "#2a2a2a" : "#fff",
      color: isDark ? "#f8f9fa" : "#111",
      borderColor: isDark ? "#3a3a3a" : base.borderColor,
      boxShadow: "none",
      minHeight: "38px",
      ":hover": {
        borderColor: isDark ? "#3a3a3a" : base.borderColor,
      },
    }),

    valueContainer: (base) => ({
      ...base,
      padding: "2px 8px",
    }),

    singleValue: (base) => ({
      ...base,
      color: isDark ? "#f8f9fa" : "#111",
    }),

    input: (base) => ({
      ...base,
      color: isDark ? "#f8f9fa" : "#111",
    }),

    placeholder: (base) => ({
      ...base,
      color: isDark ? "#f8f9fa" : "#6c757d",
      opacity: isDark ? 0.7 : 1,
    }),

    menu: (base) => ({
      ...base,
      backgroundColor: isDark ? "#2a2a2a" : "#fff",
      border: isDark ? "1px solid #3a3a3a" : base.border,
      zIndex: 9999,
    }),

    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? (isDark ? "#3a3a3a" : "#e9ecef")
        : state.isFocused
          ? (isDark ? "#333333" : "#f6f9fc")
          : "transparent",
      color: isDark ? "#f8f9fa" : "#111",
      ":active": {
        backgroundColor: isDark ? "#3a3a3a" : "#e9ecef",
      },
    }),

    multiValue: (base) => ({
      ...base,
      backgroundColor: isDark ? "#3a3a3a" : "#e9ecef",
    }),

    multiValueLabel: (base) => ({
      ...base,
      color: isDark ? "#f8f9fa" : "#111",
    }),

    multiValueRemove: (base) => ({
      ...base,
      color: isDark ? "#f8f9fa" : "#111",
      ":hover": {
        backgroundColor: isDark ? "#444444" : "#dee2e6",
        color: isDark ? "#f8f9fa" : "#111",
      },
    }),
  };

  const [deviceData, setDeviceData] = useState({
    device_name: "",
    device_model: "",
    device_ip: "",
    device_mac: "",
    device_firmware: "2.46",
    device_button: [],
    quick_dial_button_count: 1,
    device_button_assignments: {},
    extension_uuid: "",
    site_uuid: "",
    last_checkin: null,
    dir_uuids: [],
  });

  const [loading, setLoading] = useState(editing);
  const [saveStatus, setSaveStatus] = useState({ show: false, success: false, message: "" });

  const labelStyle = {
    color: isDark ? "#ffffff" : "#000000",
  };

  const sectionHeadingStyle = {
    color: theme === "dark" ? "#ffffff" : "#172b4d",
    fontWeight: "700",
    fontSize: "1.00rem",
    textTransform: "uppercase",
    marginBottom: "1rem",
  };

  const collapsibleHeaderStyle = {
    backgroundColor: isDark ? "#1f2937" : "#ffffff",
    borderBottom: "none",
    paddingTop: "1rem",
    paddingBottom: "1rem",
    cursor: "pointer",
  };

  const tenantPickerSecondaryButtonStyle = {
    backgroundColor: isDark ? "#374151" : undefined,
    borderColor: isDark ? "#4b5563" : undefined,
    color: isDark ? "#f9fafb" : undefined,
  };

  const tenantPickerSecondaryButtonDisabledStyle = {
    ...tenantPickerSecondaryButtonStyle,
    opacity: isDark ? 0.55 : undefined,
    cursor: "not-allowed",
  };

  const removeTenantButtonStyle = {
    backgroundColor: isDark ? "#7f1d1d" : undefined,
    borderColor: isDark ? "#991b1b" : undefined,
    color: isDark ? "#ffffff" : undefined,
    fontWeight: 600,
    padding: "0.25rem 0.6rem",
    lineHeight: 1.2,
  };

  const removeTenantButtonDisabledStyle = {
    ...removeTenantButtonStyle,
    opacity: isDark ? 0.55 : undefined,
    cursor: "not-allowed",
  };

  const renderCollapseIcon = (open) => (
    <div
      style={{
        color: isDark ? "#d1d5db" : "#495057",
        fontSize: "1.2rem",
        fontWeight: 700,
        userSelect: "none",
      }}
    >
      {open ? "−" : "+"}
    </div>
  );

  const emptyQuickSetupStatus = {
    show: false,
    success: false,
    message: "",
  };

  const isRootFolderUnsupportedMessage = (message) =>
    String(message || "").includes("Root folder display is only supported for 2N IP Style devices");

  const siteUuid = getSite()?.site_uuid;

  const latestEventsRequestRef = useRef(0);

  const supportsDeviceButton = ["2N IP Verso", "2N IP Solo"].includes(deviceData.device_model);
  const supportsMultipleQuickDialButtons = deviceData.device_model === "2N IP Verso";
  const isAkuvoxS539 = deviceData.device_model === AKUVOX_S539_MODEL;
  const supportsCurrentDeviceApi = ACTIVITY_DEVICE_MODELS.has(deviceData.device_model);

  const normalizeDeviceButtonValue = (value) => {
    if (Array.isArray(value)) {
      return value.flatMap(normalizeDeviceButtonValue);
    }

    if (value === null || value === undefined || value === "") {
      return [];
    }

    const text = String(value).trim();

    if (!text) {
      return [];
    }

    // Handles Postgres array text coming back from the API: "{uuid1,uuid2}"
    if (text.startsWith("{") && text.endsWith("}")) {
      const inner = text.slice(1, -1).trim();

      if (!inner) {
        return [];
      }

      return inner
        .split(",")
        .map(v => v.trim().replace(/^"|"$/g, ""))
        .filter(Boolean);
    }

    return [text];
  };

  const clampQuickDialButtonCount = (value) => {
    const parsed = Number.parseInt(value, 10);

    if (Number.isNaN(parsed)) {
      return 1;
    }

    return Math.min(MAX_QUICK_DIAL_BUTTON_COUNT, Math.max(1, parsed));
  };

  const getEffectiveQuickDialButtonCount = (
    model = deviceData.device_model,
    rawCount = deviceData.quick_dial_button_count
  ) => {
    return model === "2N IP Verso"
      ? clampQuickDialButtonCount(rawCount)
      : 1;
  };

  const normalizeDeviceButtonAssignments = (assignments, fallbackButtonOne = []) => {
    const normalized = {};

    if (assignments && typeof assignments === "object" && !Array.isArray(assignments)) {
      Object.entries(assignments).forEach(([buttonPosition, tenantUuids]) => {
        const buttonNumber = Number.parseInt(buttonPosition, 10);

        if (!buttonNumber || buttonNumber < 1) {
          return;
        }

        const tenantIds = normalizeDeviceButtonValue(tenantUuids);

        if (tenantIds.length > 0) {
          normalized[String(buttonNumber)] = tenantIds;
        }
      });

      return normalized;
    }

    const fallbackTenantIds = normalizeDeviceButtonValue(fallbackButtonOne);

    if (fallbackTenantIds.length > 0) {
      normalized["1"] = fallbackTenantIds;
    }

    return normalized;
  };

  const buildButtonAssignmentState = (count, existing = {}) => {
    const safeCount = clampQuickDialButtonCount(count);
    const next = {};

    for (let buttonNumber = 1; buttonNumber <= safeCount; buttonNumber += 1) {
      next[String(buttonNumber)] =
        normalizeDeviceButtonValue(existing[String(buttonNumber)] || existing[buttonNumber] || []);
    }

    return next;
  };

  const getTenantOptionByUuid = (tenantUuid) => {
    const tenant = directoryTenants.find(
      (t) => String(t.tenant_uuid) === String(tenantUuid)
    );

    return tenant
      ? {
        value: tenant.tenant_uuid,
        label: tenant.user_name,
      }
      : null;
  };

  const getButtonTenantSelectValue = (buttonNumber) => {
    const buttonKey = String(buttonNumber);
    const tenantUuids = deviceData.device_button_assignments?.[buttonKey] || [];

    return tenantUuids
      .map(getTenantOptionByUuid)
      .filter(Boolean);
  };

  const handleQuickDialButtonCountChange = (e) => {
    const rawValue = e.target.value;

    // Solo is fixed to one button, so this input should only affect Verso.
    if (!supportsMultipleQuickDialButtons) {
      setDeviceData((prev) => ({
        ...prev,
        quick_dial_button_count: 1,
        device_button_assignments: buildButtonAssignmentState(
          1,
          prev.device_button_assignments || {}
        ),
      }));
      return;
    }

    // Allow the user to erase the field before typing a new value.
    if (rawValue === "") {
      setDeviceData((prev) => ({
        ...prev,
        quick_dial_button_count: "",
      }));
      return;
    }

    // Keep only positive whole numbers.
    if (!/^\d+$/.test(rawValue)) {
      return;
    }

    const nextCount = clampQuickDialButtonCount(rawValue);

    setDeviceData((prev) => ({
      ...prev,
      quick_dial_button_count: nextCount,
      device_button_assignments: buildButtonAssignmentState(
        nextCount,
        prev.device_button_assignments || {}
      ),
    }));
  };

  const handleQuickDialButtonCountBlur = () => {
    if (!supportsMultipleQuickDialButtons) {
      setDeviceData((prev) => ({
        ...prev,
        quick_dial_button_count: 1,
        device_button_assignments: buildButtonAssignmentState(
          1,
          prev.device_button_assignments || {}
        ),
      }));
      return;
    }

    const nextCount = clampQuickDialButtonCount(deviceData.quick_dial_button_count);

    setDeviceData((prev) => ({
      ...prev,
      quick_dial_button_count: nextCount,
      device_button_assignments: buildButtonAssignmentState(
        nextCount,
        prev.device_button_assignments || {}
      ),
    }));
  };

  const handleButtonTenantChange = (buttonNumber, selectedOptions) => {
    const selected = selectedOptions || [];

    if (selected.length > MAX_TENANTS_PER_DEVICE_BUTTON) {
      setSaveStatus({
        show: true,
        success: false,
        message: `Button ${buttonNumber} can have at most ${MAX_TENANTS_PER_DEVICE_BUTTON} tenants.`,
      });
      return;
    }

    const buttonKey = String(buttonNumber);

    setDeviceData((prev) => ({
      ...prev,
      device_button_assignments: {
        ...(prev.device_button_assignments || {}),
        [buttonKey]: selected.map((opt) => opt.value),
      },
    }));
  };

  const getTenantByUuid = (tenantUuid) => {
    return directoryTenants.find(
      (tenant) => String(tenant.tenant_uuid) === String(tenantUuid)
    ) || null;
  };

  const getTenantName = (tenant) => {
    return tenant?.user_name || tenant?.name || "—";
  };

  const getTenantVirtualNumber = (tenant) => {
    return (
      tenant?.v_number ||
      tenant?.virtNumber ||
      tenant?.virtual_number ||
      tenant?.virtualNumber ||
      ""
    );
  };

  const getTenantPhone1 = (tenant) => {
    return (
      tenant?.phone1 ||
      tenant?.phone_1 ||
      tenant?.phoneNumber1 ||
      tenant?.phone_number_1 ||
      ""
    );
  };

  const getSelectedTenantsForButton = (buttonNumber) => {
    const buttonKey = String(buttonNumber);
    const tenantUuids = normalizeDeviceButtonValue(
      deviceData.device_button_assignments?.[buttonKey] || []
    );

    return tenantUuids.map((tenantUuid) => {
      return getTenantByUuid(tenantUuid) || {
        tenant_uuid: tenantUuid,
        user_name: "Unknown tenant",
        v_number: "",
        phone1: "",
      };
    });
  };

  const removeTenantFromButton = (buttonNumber, tenantUuid) => {
    const buttonKey = String(buttonNumber);

    setDeviceData((prev) => {
      const current = normalizeDeviceButtonValue(
        prev.device_button_assignments?.[buttonKey] || []
      );

      return {
        ...prev,
        device_button_assignments: {
          ...(prev.device_button_assignments || {}),
          [buttonKey]: current.filter(
            (id) => String(id) !== String(tenantUuid)
          ),
        },
      };
    });
  };

  const openTenantPicker = (buttonNumber) => {
    const buttonKey = String(buttonNumber);
    const current = normalizeDeviceButtonValue(
      deviceData.device_button_assignments?.[buttonKey] || []
    );

    setTenantPickerButtonNumber(buttonNumber);
    setTenantPickerSelected(current);
    setTenantPickerSearch("");
    setTenantPickerPage(1);
    setTenantPickerOpen(true);
  };

  const closeTenantPicker = () => {
    setTenantPickerOpen(false);
    setTenantPickerButtonNumber(null);
    setTenantPickerSelected([]);
    setTenantPickerSearch("");
    setTenantPickerPage(1);
  };

  const isTenantPickerSelected = (tenantUuid) => {
    return tenantPickerSelected.some(
      (id) => String(id) === String(tenantUuid)
    );
  };

  const toggleTenantPickerTenant = (tenantUuid) => {
    const tenantId = String(tenantUuid);

    setTenantPickerSelected((prev) => {
      const exists = prev.some((id) => String(id) === tenantId);

      if (exists) {
        return prev.filter((id) => String(id) !== tenantId);
      }

      if (prev.length >= MAX_TENANTS_PER_DEVICE_BUTTON) {
        setSaveStatus({
          show: true,
          success: false,
          message: `Button ${tenantPickerButtonNumber} can have at most ${MAX_TENANTS_PER_DEVICE_BUTTON} tenants.`,
        });
        return prev;
      }

      return [...prev, tenantId];
    });
  };

  const saveTenantPickerSelection = () => {
    if (!tenantPickerButtonNumber) return;

    if (tenantPickerSelected.length > MAX_TENANTS_PER_DEVICE_BUTTON) {
      setSaveStatus({
        show: true,
        success: false,
        message: `Button ${tenantPickerButtonNumber} can have at most ${MAX_TENANTS_PER_DEVICE_BUTTON} tenants.`,
      });
      return;
    }

    const buttonKey = String(tenantPickerButtonNumber);

    setDeviceData((prev) => ({
      ...prev,
      device_button_assignments: {
        ...(prev.device_button_assignments || {}),
        [buttonKey]: tenantPickerSelected,
      },
    }));

    closeTenantPicker();
  };

  const getAlreadyAssignedTenantIdsForPicker = () => {
    if (!tenantPickerButtonNumber) {
      return new Set();
    }

    const buttonKey = String(tenantPickerButtonNumber);
    const assignedTenantIds = normalizeDeviceButtonValue(
      deviceData.device_button_assignments?.[buttonKey] || []
    );

    return new Set(assignedTenantIds.map((id) => String(id)));
  };

  const tenantPickerAlreadyAssignedIds = getAlreadyAssignedTenantIdsForPicker();

  const tenantPickerFilteredTenants = directoryTenants.filter((tenant) => {
    const tenantId = String(tenant.tenant_uuid);

    // Do not show tenants already assigned to this button.
    if (tenantPickerAlreadyAssignedIds.has(tenantId)) {
      return false;
    }

    const search = tenantPickerSearch.trim().toLowerCase();

    if (!search) return true;

    const searchableText = [
      getTenantName(tenant),
      getTenantVirtualNumber(tenant),
      getTenantPhone1(tenant),
    ]
      .join(" ")
      .toLowerCase();

    return searchableText.includes(search);
  });

  const tenantPickerTotalPages = Math.max(
    1,
    Math.ceil(tenantPickerFilteredTenants.length / TENANT_PICKER_PAGE_SIZE)
  );

  const tenantPickerSafePage = Math.min(
    tenantPickerPage,
    tenantPickerTotalPages
  );

  const tenantPickerPageTenants = tenantPickerFilteredTenants.slice(
    (tenantPickerSafePage - 1) * TENANT_PICKER_PAGE_SIZE,
    tenantPickerSafePage * TENANT_PICKER_PAGE_SIZE
  );

  const fileToDataUrl = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Failed to read image file"));

      reader.readAsDataURL(file);
    });
  };

  const dataUrlToBytes = (dataUrl) => {
    return Math.ceil((dataUrl || "").length * 0.75);
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", reject);
      image.setAttribute("crossOrigin", "anonymous");
      image.src = url;
    });

  const getCroppedImage = async (imageSrc) => {
    const image = await createImage(imageSrc);

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const size = 432;
    canvas.width = size;
    canvas.height = size;

    const isPng = imageSrc.startsWith("data:image/png");
    const outputType = isPng ? "image/png" : "image/jpeg";

    if (!isPng) {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
    }

    const baseScale = Math.min(size / image.width, size / image.height);
    const finalScale = baseScale * zoom;

    const drawWidth = image.width * finalScale;
    const drawHeight = image.height * finalScale;

    const drawX = (size - drawWidth) / 2 + panX;
    const drawY = (size - drawHeight) / 2 + panY;

    ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);

    let dataUrl;

    if (outputType === "image/jpeg") {
      let quality = 0.9;

      do {
        dataUrl = canvas.toDataURL("image/jpeg", quality);
        quality -= 0.05;
      } while (
        dataUrlToBytes(dataUrl) > MAX_DIRECTORY_IMAGE_BYTES &&
        quality > 0.4
      );
    } else {
      dataUrl = canvas.toDataURL("image/png");

      if (dataUrlToBytes(dataUrl) > MAX_DIRECTORY_IMAGE_BYTES) {
        throw new Error("PNG image is too large. Try zooming out or using a simpler image.");
      }
    }

    return dataUrl;
  };

  const openDirectoryImageEditor = async (dirUuid, file = null, existingImage = null) => {
    const imageDataUrl = file ? await fileToDataUrl(file) : existingImage;

    if (!imageDataUrl) return;

    setImageEditorDirUuid(dirUuid);
    setImageEditorSrc(imageDataUrl);
    setImageEditorFileName(file?.name || "");
    setCrop({ x: 0, y: 0 });
    setZoom(1.0);
    setPanX(0);
    setPanY(0);
    setCroppedAreaPixels(null);
    setImageEditorError("");
    setImageEditorResultTooLarge(false);
    setImageEditorResultBytes(0);
    setImageEditorOpen(true);
  };

  const closeDirectoryImageEditor = () => {
    setImageEditorOpen(false);
    setImageEditorDirUuid(null);
    setImageEditorSrc(null);
    setImageEditorFileName("");
    setImageEditorError("");
    setImageEditorResultTooLarge(false);
    setImageEditorResultBytes(0);
  };

  const saveEditedDirectoryImage = async () => {
    if (!imageEditorDirUuid || !imageEditorSrc) return;

    try {
      const croppedImage = await getCroppedImage(imageEditorSrc);
      const croppedBytes = dataUrlToBytes(croppedImage);

      if (croppedBytes > MAX_DIRECTORY_IMAGE_BYTES) {
        setImageEditorResultTooLarge(true);
        setImageEditorResultBytes(croppedBytes);
        setImageEditorError("The resultant image is too large. Try another format.");
        return;
      }

      if (dataUrlToBytes(croppedImage) > MAX_DIRECTORY_IMAGE_BYTES) {
        setImageEditorError("The resultant image is too large. Try another format.");
        return;
      }

      setDirectoryDisplaySettings((prev) => ({
        ...prev,
        [imageEditorDirUuid]: {
          ...(prev[imageEditorDirUuid] || {}),
          image: croppedImage,
          imageName: imageEditorFileName || "Image selected",
          selectedColor: "",
          removeImage: false,
        },
      }));

      closeDirectoryImageEditor();
    } catch (err) {
      console.error("Failed to save edited image:", err);
      setImageEditorError(err.message || "Failed to crop image. Please try another image.");
    }
  };

  const handleDirectoryColorSelect = async (dirUuid, option) => {
    if (!dirUuid || !option || isBusy) return;

    setQuickSetupStatus({ show: false, success: false, message: "" });

    try {
      const dataUrl = await createSolidColorDirectoryImageDataUrl(option.hex);
      const bytes = dataUrlToBytes(dataUrl);

      if (bytes > MAX_DIRECTORY_IMAGE_BYTES) {
        setQuickSetupStatus({
          show: true,
          success: false,
          message: "The selected color image is too large. Please try another color.",
        });
        return;
      }

      setDirectoryDisplaySettings((prev) => ({
        ...prev,
        [dirUuid]: {
          ...(prev[dirUuid] || {}),
          image: dataUrl,
          imageName: `${option.label} color`,
          selectedColor: option.hex,
          removeImage: false,
        },
      }));
    } catch (err) {
      console.error("Failed to create directory color image:", err);
      setQuickSetupStatus({
        show: true,
        success: false,
        message: err.message || "Failed to create directory color image.",
      });
    }
  };

  useEffect(() => {
    if (!siteUuid) return;

    const loadDirectories = async () => {
      try {
        const res = await fetch(
          `${serverHost}/hrms/get_directories?site_uuid=${siteUuid}`
        );
        if (!res.ok) throw new Error("Failed to fetch directories");
        const data = await res.json();
        setDirectories(data);
      } catch (err) {
        console.error("Failed to load directories:", err);
      }
    };

    loadDirectories();
  }, [siteUuid]);

  useEffect(() => {
    if (!siteUuid) return;

    const loadExtensions = async () => {
      try {
        const res = await fetch(
          `${serverHost}/hrms/hucs/extensions/by-site/${siteUuid}`
        );
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data?.error || "Failed to fetch HUCS extensions");
        }

        setExtensionOptions(data || []);
      } catch (err) {
        console.error("Failed to load HUCS extensions:", err);
        setExtensionOptions([]);
      }
    };

    loadExtensions();
  }, [siteUuid]);

  useEffect(() => {
    if (!deviceData.dir_uuids || deviceData.dir_uuids.length === 0) {
      setDirectoryTenants([]);
      return;
    }

    const loadDirectoryTenants = async () => {
      try {
        // Fetch tenants for each selected directory
        const tenantsArrays = await Promise.all(
          deviceData.dir_uuids.map(async (dir_uuid) => {
            const res = await fetch(
              `${serverHost}/hrms/get_directory_tenants?dir_uuid=${dir_uuid}&site_uuid=${siteUuid}`
            );
            if (!res.ok) throw new Error("Failed to fetch tenants");
            return await res.json();
          })
        );

        // Flatten and remove duplicates
        const allTenants = tenantsArrays.flat();
        const uniqueTenants = Array.from(
          new Map(allTenants.map(t => [t.tenant_uuid, t])).values()
        );

        setDirectoryTenants(uniqueTenants);
      } catch (err) {
        console.error("Failed to load tenants:", err);
        setDirectoryTenants([]);
      }
    };

    loadDirectoryTenants();
  }, [deviceData.dir_uuids]);

  // Load device if editing
  useEffect(() => {
    if (!editing) return;

    const site = getSite();
    if (!site?.site_uuid) return;

    const loadDevice = async () => {
      try {
        const [deviceRes, dirsRes] = await Promise.all([
          fetch(`${serverHost}/hrms/get_device/${device_uuid}`),
          fetch(`${serverHost}/hrms/get_device_dir?device_uuid=${device_uuid}&site_uuid=${site.site_uuid}`),
        ]);

        if (!deviceRes.ok) throw new Error("Failed to fetch device");

        const data = await deviceRes.json();
        const dirsData = dirsRes.ok ? await dirsRes.json() : [];

        const loadedDeviceModel = data.device_model || "";

        const loadedButtonCount = loadedDeviceModel === "2N IP Verso"
          ? clampQuickDialButtonCount(data.quick_dial_button_count || 1)
          : 1;

        const loadedAssignments = buildButtonAssignmentState(
          loadedButtonCount,
          normalizeDeviceButtonAssignments(
            data.device_button_assignments,
            data.device_button
          )
        );

        setDeviceData({
          device_name: data.device_name || "",
          device_model: loadedDeviceModel,
          device_ip: data.device_ip || "",
          device_mac: data.device_mac || "",
          device_firmware: data.device_firmware || (
            loadedDeviceModel === AKUVOX_S539_MODEL
              ? "539.30.10.130"
              : "2.46"
          ),
          device_button: normalizeDeviceButtonValue(data.device_button),
          quick_dial_button_count: loadedButtonCount,
          device_button_assignments: loadedAssignments,
          extension_uuid: data.extension_uuid || "",
          site_uuid: data.site_uuid || "",
          last_checkin: data.last_checkin || null,
          dir_uuids: dirsData.map((d) => d.dir_uuid || d.directory_uuid),
        });
        const loadedBackground = data.background_image || null;
        setBackgroundPreview(loadedBackground);
        setSavedBackgroundPreview(loadedBackground);
      } catch (err) {
        console.error("Failed to fetch device:", err);
        setSaveStatus({ show: true, success: false, message: "Failed to load device" });
      } finally {
        setLoading(false);
      }
    };

    loadDevice();
  }, [device_uuid, editing]);

  useEffect(() => {
    if (!editing || !device_uuid) return;
    if (deviceData.device_model !== "2N IP Style") return;

    loadRfidBacklightState();
  }, [editing, device_uuid, deviceData.device_model]);

  useEffect(() => {
    if (!editing || !device_uuid) return;
    if (deviceData.device_model !== "2N IP Style") return;

    loadShowcaseModeState();
  }, [editing, device_uuid, deviceData.device_model]);

  useEffect(() => {
    if (!editing || !device_uuid) return;
    if (!supportsCurrentDeviceApi) return;
    loadCallLogs();
  }, [editing, device_uuid, callLogsRange, supportsCurrentDeviceApi]);

  useEffect(() => {
    if (!editing || !device_uuid) return;
    if (deviceData.device_model !== "2N IP Style") return;

    loadRootFolderDisplayState();
  }, [editing, device_uuid, deviceData.device_model]);

  useEffect(() => {
    if (!editing || !device_uuid) return;
    if (deviceData.device_model !== "2N IP Style") return;

    loadStyleDisplaySettingsState();
  }, [editing, device_uuid, deviceData.device_model]);

  useEffect(() => {
    if (!editing || !device_uuid) return;
    if (deviceData.device_model !== "2N IP Verso") return;

    loadVersoDisplaySettingsState();
  }, [editing, device_uuid, deviceData.device_model]);

  useEffect(() => {
    if (!liveEventsOpen) return;
    if (!device_uuid) return;

    loadEvents();
  }, [liveEventsOpen, device_uuid, eventsRange]);

  useEffect(() => {
    if (!deviceData.dir_uuids || deviceData.dir_uuids.length === 0) {
      setDirectoryDisplaySettings({});
      setInitialDirectoryDisplaySettings({});
      return;
    }

    setDirectoryDisplaySettings((prev) => {
      const next = {};

      deviceData.dir_uuids.forEach((dirUuid) => {
        next[dirUuid] = prev[dirUuid] || {
          priority: "",
          image: null,
          imageName: "",
          removeImage: false,
        };
      });

      return next;
    });
  }, [deviceData.dir_uuids]);

  useEffect(() => {
    if (!imageEditorOpen) return;

    const el = imageEditorRef.current;
    if (!el) return;

    const handleWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();

      const delta = e.deltaY > 0 ? -0.08 : 0.08;

      setZoom((prev) => {
        const next = prev + delta;
        return Math.min(3, Math.max(0.1, next));
      });
    };

    el.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      el.removeEventListener("wheel", handleWheel);
    };
  }, [imageEditorOpen]);

  useEffect(() => {
    if (!editing || !device_uuid) return;
    if (!deviceData.dir_uuids || deviceData.dir_uuids.length === 0) return;

    loadDirectoryDisplaySettings();
  }, [editing, device_uuid, deviceData.dir_uuids]);

  useEffect(() => {
    if (!imageEditorOpen || !imageEditorSrc) return;

    const timeout = setTimeout(async () => {
      try {
        const croppedImage = await getCroppedImage(imageEditorSrc);
        const bytes = dataUrlToBytes(croppedImage);

        setImageEditorResultBytes(bytes);
        setImageEditorResultTooLarge(bytes > MAX_DIRECTORY_IMAGE_BYTES);

        if (bytes > MAX_DIRECTORY_IMAGE_BYTES) {
          setImageEditorError("The resultant image is too large. Try zooming out or choosing a simpler image.");
        } else {
          setImageEditorError("");
        }
      } catch (err) {
        console.error("Failed to check cropped image size:", err);
      }
    }, 250);

    return () => clearTimeout(timeout);
  }, [imageEditorOpen, imageEditorSrc, zoom, panX, panY]);

  useEffect(() => {
    if (!displaySettingsOpen) return;

    if (
      deviceData.device_model === "2N IP Verso" &&
      isRootFolderUnsupportedMessage(quickSetupStatus.message)
    ) {
      setQuickSetupStatus(emptyQuickSetupStatus);
    }
  }, [displaySettingsOpen, deviceData.device_model, quickSetupStatus.message]);

  const loadRfidBacklightState = async () => {
    if (!editing || !device_uuid || deviceData.device_model !== "2N IP Style") return;

    setRfidBacklightLoading(true);

    try {
      const res = await fetch(
        `${serverHost}/hrms/device_quick_setup/rfid_backlight/${device_uuid}`
      );

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.error || "Failed to load RFID backlight state");
      }

      const enabled = !!result.enabled;
      setRfidBacklightEnabled(enabled);
      setInitialRfidBacklightEnabled(enabled);
    } catch (err) {
      console.error("Load RFID backlight state error:", err);
      setQuickSetupStatus({
        show: true,
        success: false,
        message: err.message || "Failed to load RFID backlight state",
      });
    } finally {
      setRfidBacklightLoading(false);
    }
  };

  const handleRfidBacklightToggle = (e) => {
    setRfidBacklightEnabled(e.target.checked);
    setQuickSetupStatus({ show: false, success: false, message: "" });
  };

  const loadShowcaseModeState = async () => {
    if (!editing || !device_uuid || deviceData.device_model !== "2N IP Style") return;

    setShowcaseModeLoading(true);

    try {
      const res = await fetch(
        `${serverHost}/hrms/device_quick_setup/showcase_mode/${device_uuid}`
      );

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.error || "Failed to load showcase mode");
      }

      const mode = result.mode || "OFF";
      setShowcaseMode(mode);
      setInitialShowcaseMode(mode);
    } catch (err) {
      console.error("Load showcase mode error:", err);
      setQuickSetupStatus({
        show: true,
        success: false,
        message: err.message || "Failed to load showcase mode",
      });
    } finally {
      setShowcaseModeLoading(false);
    }
  };

  const handleShowcaseModeChange = (e) => {
    setShowcaseMode(e.target.value);
    setQuickSetupStatus({ show: false, success: false, message: "" });
  };

  const loadRootFolderDisplayState = async () => {
    if (!editing || !device_uuid || deviceData.device_model !== "2N IP Style") return;

    setRootFolderDisplayLoading(true);

    try {
      const res = await fetch(
        `${serverHost}/hrms/device_quick_setup/root_folder_display/${device_uuid}`
      );

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.error || "Failed to load root folder display");
      }

      const mode = result.mode || "CARDS_1_COLUMN";
      setRootFolderDisplay(mode);
      setInitialRootFolderDisplay(mode);
    } catch (err) {
      setQuickSetupStatus({
        show: true,
        success: false,
        message: err.message || "Failed to load root folder display",
      });
    } finally {
      setRootFolderDisplayLoading(false);
    }
  };

  const handleRootFolderDisplayChange = (e) => {
    setRootFolderDisplay(e.target.value);
    setQuickSetupStatus({ show: false, success: false, message: "" });
  };

  const normalizeStyleDisplaySettings = (settings = {}) => {
    const rawMode = settings.entry_keypad_mode ?? settings.entryKeypadMode ?? "NORMAL";
    const normalizedMode = String(rawMode || "NORMAL").trim().toUpperCase();

    return {
      codeEntryButton: settings.code_entry_button ?? settings.codeEntryButton ?? true,
      entryKeypadMode: ["NORMAL", "SCRAMBLED"].includes(normalizedMode)
        ? normalizedMode
        : "NORMAL",
      touchSounds: settings.touch_sounds ?? settings.touchSounds ?? true,
      flexiBuilderEnabled: settings.flexi_builder_enabled ?? settings.flexiBuilderEnabled ?? false,
    };
  };

  const toStyleDisplaySettingsPayload = (settings = {}) => ({
    code_entry_button: !!settings.codeEntryButton,
    entry_keypad_mode: settings.entryKeypadMode || "NORMAL",
    touch_sounds: !!settings.touchSounds,
    flexi_builder_enabled: !!settings.flexiBuilderEnabled,
  });

  const loadStyleDisplaySettingsState = async () => {
    if (!editing || !device_uuid || deviceData.device_model !== "2N IP Style") return;

    setStyleDisplaySettingsLoading(true);

    try {
      const res = await fetch(
        `${serverHost}/hrms/device_quick_setup/style_display_settings/${device_uuid}`
      );

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.error || "Failed to load Style display settings");
      }

      const normalized = normalizeStyleDisplaySettings(result.settings || {});
      setStyleDisplaySettings(normalized);
      setInitialStyleDisplaySettings(normalized);
    } catch (err) {
      console.error("Load Style display settings error:", err);
      setQuickSetupStatus({
        show: true,
        success: false,
        message: err.message || "Failed to load Style display settings",
      });
    } finally {
      setStyleDisplaySettingsLoading(false);
    }
  };

  const handleStyleDisplayToggle = (field) => (e) => {
    setStyleDisplaySettings((prev) => ({
      ...prev,
      [field]: e.target.checked,
    }));

    setQuickSetupStatus({ show: false, success: false, message: "" });
  };

  const handleStyleKeypadModeChange = (e) => {
    setStyleDisplaySettings((prev) => ({
      ...prev,
      entryKeypadMode: e.target.value,
    }));

    setQuickSetupStatus({ show: false, success: false, message: "" });
  };

  const saveStyleDisplaySettingsIfChanged = async () => {
    if (!editing || !device_uuid || deviceData.device_model !== "2N IP Style") return;

    if (JSON.stringify(styleDisplaySettings) === JSON.stringify(initialStyleDisplaySettings)) {
      return;
    }

    const res = await fetch(
      `${serverHost}/hrms/device_quick_setup/style_display_settings/${device_uuid}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          settings: toStyleDisplaySettingsPayload(styleDisplaySettings),
        }),
      }
    );

    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(result.error || "Failed to update Style display settings");
    }

    const normalized = normalizeStyleDisplaySettings(result.settings || styleDisplaySettings);
    setStyleDisplaySettings(normalized);
    setInitialStyleDisplaySettings(normalized);
  };

  const normalizeVersoDisplaySettings = (settings = {}) => ({
    phonebookDisplayed: settings.phonebook_displayed ?? settings.phonebookDisplayed ?? true,
    codeEntryButton: settings.code_entry_button ?? settings.codeEntryButton ?? true,
    entryKeypadMode: settings.entry_keypad_mode ?? settings.entryKeypadMode ?? "NORMAL",
    preferIconsToText: settings.prefer_icons_to_text ?? settings.preferIconsToText ?? false,
  });

  const toVersoDisplaySettingsPayload = (settings = {}) => ({
    phonebook_displayed: !!settings.phonebookDisplayed,
    code_entry_button: !!settings.codeEntryButton,
    entry_keypad_mode: settings.entryKeypadMode || "NORMAL",
    prefer_icons_to_text: !!settings.preferIconsToText,
  });

  const loadVersoDisplaySettingsState = async () => {
    if (!editing || !device_uuid || deviceData.device_model !== "2N IP Verso") return;

    setVersoDisplaySettingsLoading(true);

    try {
      const res = await fetch(
        `${serverHost}/hrms/device_quick_setup/verso_display_settings/${device_uuid}`
      );

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.error || "Failed to load Verso display settings");
      }

      const normalized = normalizeVersoDisplaySettings(result.settings || {});
      setVersoDisplaySettings(normalized);
      setInitialVersoDisplaySettings(normalized);
    } catch (err) {
      console.error("Load Verso display settings error:", err);
      setQuickSetupStatus({
        show: true,
        success: false,
        message: err.message || "Failed to load Verso display settings",
      });
    } finally {
      setVersoDisplaySettingsLoading(false);
    }
  };

  const handleVersoDisplayToggle = (field) => (e) => {
    setVersoDisplaySettings((prev) => ({
      ...prev,
      [field]: e.target.checked,
    }));

    setQuickSetupStatus({ show: false, success: false, message: "" });
  };

  const handleVersoKeypadModeChange = (e) => {
    setVersoDisplaySettings((prev) => ({
      ...prev,
      entryKeypadMode: e.target.value,
    }));

    setQuickSetupStatus({ show: false, success: false, message: "" });
  };

  const saveVersoDisplaySettingsIfChanged = async () => {
    if (!editing || !device_uuid || deviceData.device_model !== "2N IP Verso") return;

    if (JSON.stringify(versoDisplaySettings) === JSON.stringify(initialVersoDisplaySettings)) {
      return;
    }

    const res = await fetch(
      `${serverHost}/hrms/device_quick_setup/verso_display_settings/${device_uuid}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          settings: toVersoDisplaySettingsPayload(versoDisplaySettings),
        }),
      }
    );

    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(result.error || "Failed to update Verso display settings");
    }

    const normalized = normalizeVersoDisplaySettings(result.settings || versoDisplaySettings);
    setVersoDisplaySettings(normalized);
    setInitialVersoDisplaySettings(normalized);
  };

  const saveRootFolderDisplayIfChanged = async () => {
    if (!editing || !device_uuid || deviceData.device_model !== "2N IP Style") return;

    if (rootFolderDisplay === initialRootFolderDisplay) {
      return;
    }

    const res = await fetch(
      `${serverHost}/hrms/device_quick_setup/root_folder_display/${device_uuid}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: rootFolderDisplay }),
      }
    );

    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(result.error || "Failed to update root folder display");
    }

    setInitialRootFolderDisplay(rootFolderDisplay);
  };

  const saveDirectoryDisplaySettingsIfChanged = async () => {
    if (!editing || !device_uuid) return;

    const initial = JSON.stringify(initialDirectoryDisplaySettings);
    const current = JSON.stringify(directoryDisplaySettings);

    if (initial === current) {
      return;
    }

    const updates = Object.entries(directoryDisplaySettings).map(([dir_uuid, settings]) => ({
      dir_uuid,
      priority: settings.priority === "" ? null : Number(settings.priority),
      image: settings.image || null,
      remove_image: !!settings.removeImage,
    }));

    const res = await fetch(
      `${serverHost}/hrms/device_directory_display_settings/${device_uuid}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ directories: updates }),
      }
    );

    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(result.error || "Failed to update directory display settings");
    }

    setInitialDirectoryDisplaySettings({ ...directoryDisplaySettings });
  };

  const loadDirectoryDisplaySettings = async () => {
    if (!editing || !device_uuid) return;

    try {
      const res = await fetch(
        `${serverHost}/hrms/device_directory_display_settings/${device_uuid}`
      );

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.error || "Failed to load directory display settings");
      }

      const settings = {};

      (result.directories || []).forEach((dir) => {
        settings[dir.dir_uuid] = {
          priority: dir.priority === null || dir.priority === undefined ? "" : String(dir.priority),
          image: dir.image || null,
          removeImage: false,
        };
      });

      setDirectoryDisplaySettings(settings);
      setInitialDirectoryDisplaySettings(settings);
    } catch (err) {
      console.error("Failed to load directory display settings:", err);
    }
  };

  const clearBackgroundInputElement = () => {
    const input = document.getElementById("input-device-background");
    if (input) input.value = "";
  };

  const clearPendingBackgroundSelection = () => {
    setBackgroundFile(null);
    setBackgroundPreview(savedBackgroundPreview);
    setBackgroundMeta({
      width: null,
      height: null,
      validDimensions: false,
    });
    setBackgroundStatus({ show: false, success: false, message: "" });
    clearBackgroundInputElement();
  };

  const handleBackgroundFileChange = async (e) => {
    const file = e.target.files?.[0] || null;
    setBackgroundPreview(null);
    setBackgroundFile(null);
    setBackgroundMeta({
      width: null,
      height: null,
      validDimensions: false,
    });
    setBackgroundStatus({ show: false, success: false, message: "" });

    if (!file) {
      setBackgroundPreview(savedBackgroundPreview);
      return;
    }

    try {
      const meta = await validateBackgroundFile(file);
      const previewUrl = URL.createObjectURL(file);

      setBackgroundFile(file);
      setBackgroundPreview(previewUrl);
      setBackgroundMeta(meta);

      if (!meta.validDimensions) {
        setBackgroundStatus({
          show: true,
          success: false,
          message: `Image must be at least 800 x 1280. Selected image is ${meta.width} x ${meta.height}.`,
        });
      }
    } catch (err) {
      setBackgroundStatus({
        show: true,
        success: false,
        message: err.message || "Invalid image file",
      });
    }
  };

  const handleBackgroundUpload = async () => {
    if (!editing || !device_uuid || isBusy) return;

    if (!backgroundFile) {
      setBackgroundStatus({
        show: true,
        success: false,
        message: "Please choose an image first",
      });
      return;
    }

    if (!backgroundMeta.validDimensions) {
      setBackgroundStatus({
        show: true,
        success: false,
        message: `Image must be at least 800 x 1280. Current image is ${backgroundMeta.width} x ${backgroundMeta.height}.`,
      });
      return;
    }

    setBackgroundUploading(true);
    setBackgroundStatus({ show: false, success: false, message: "" });

    try {
      const formData = new FormData();
      formData.append("image", backgroundFile);
      formData.append("displayLayer", "default");

      const res = await fetch(`${serverHost}/hrms/device_background_config/${device_uuid}`, {
        method: "POST",
        body: formData,
      });

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.error || "Failed to upload background image");
      }

      setBackgroundStatus({
        show: true,
        success: true,
        message: "Background image uploaded successfully",
      });

      const uploadedBackground = result.background_image || backgroundPreview;

      setBackgroundPreview(uploadedBackground);
      setSavedBackgroundPreview(uploadedBackground);
      setBackgroundFile(null);
      setBackgroundMeta({
        width: null,
        height: null,
        validDimensions: false,
      });

      clearBackgroundInputElement();
    } catch (err) {
      console.error("Background upload error:", err);
      setBackgroundStatus({
        show: true,
        success: false,
        message: err.message || "Failed to upload background image",
      });
    } finally {
      setBackgroundUploading(false);
    }
  };

  const handleRemoveBackground = async () => {
    if (!editing || !device_uuid || isBusy) return;

    setBackgroundRemoving(true);
    setBackgroundStatus({ show: false, success: false, message: "" });

    try {
      const res = await fetch(
        `${serverHost}/hrms/device_background_config/${device_uuid}`,
        { method: "DELETE" }
      );

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.error || "Failed to remove background");
      }

      setBackgroundStatus({
        show: true,
        success: true,
        message: "Background removed successfully",
      });

      setBackgroundPreview(null);
      setSavedBackgroundPreview(null);
      setBackgroundFile(null);
      setBackgroundMeta({
        width: null,
        height: null,
        validDimensions: false,
      });

      clearBackgroundInputElement();
    } catch (err) {
      console.error("Background remove error:", err);
      setBackgroundStatus({
        show: true,
        success: false,
        message: err.message || "Failed to remove background",
      });
    } finally {
      setBackgroundRemoving(false);
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    const field = id.replace("input-", "");

    if (field === "device_model") {
      const nextSupportsDeviceButton = ["2N IP Verso", "2N IP Solo"].includes(value);
      const nextButtonCount = value === "2N IP Verso"
        ? clampQuickDialButtonCount(deviceData.quick_dial_button_count)
        : 1;

      setDeviceData((prev) => {
        const nextAssignments = nextSupportsDeviceButton
          ? buildButtonAssignmentState(
            nextButtonCount,
            prev.device_button_assignments || {}
          )
          : {};

        return {
          ...prev,
          device_model: value,
          device_firmware: value === AKUVOX_S539_MODEL
            ? "539.30.10.130"
            : (
              prev.device_model === AKUVOX_S539_MODEL
                ? "2.46"
                : prev.device_firmware || "2.46"
            ),
          quick_dial_button_count: nextButtonCount,
          device_button_assignments: nextAssignments,
          device_button: nextAssignments["1"] || [],
        };
      });

      return;
    }

    setDeviceData((prev) => ({ ...prev, [field]: value }));
  };

  const saveRfidBacklightIfChanged = async () => {
    if (!editing || !device_uuid || deviceData.device_model !== "2N IP Style") return;

    if (rfidBacklightEnabled === initialRfidBacklightEnabled) {
      return;
    }

    const res = await fetch(
      `${serverHost}/hrms/device_quick_setup/rfid_backlight/${device_uuid}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: rfidBacklightEnabled }),
      }
    );

    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(result.error || "Failed to update RFID backlight");
    }

    setInitialRfidBacklightEnabled(rfidBacklightEnabled);
  };

  const saveShowcaseModeIfChanged = async () => {
    if (!editing || !device_uuid || deviceData.device_model !== "2N IP Style") return;

    if (showcaseMode === initialShowcaseMode) {
      return;
    }

    const res = await fetch(
      `${serverHost}/hrms/device_quick_setup/showcase_mode/${device_uuid}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: showcaseMode }),
      }
    );

    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(result.error || "Failed to update showcase mode");
    }

    setInitialShowcaseMode(showcaseMode);
  };

  const isValidMacAddress = (value) => {
    if (!value || String(value).trim() === "") return true;

    const cleaned = String(value).trim();

    return /^([0-9a-fA-F]{2}[:-]){5}[0-9a-fA-F]{2}$/.test(cleaned)
      || /^[0-9a-fA-F]{12}$/.test(cleaned);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (isBusy) return;

    const deviceName = String(deviceData.device_name || "").trim();
    const deviceModel = String(deviceData.device_model || "").trim();
    const deviceFirmware = String(deviceData.device_firmware || "").trim();
    const deviceIp = String(deviceData.device_ip || "").trim();
    const deviceMac = String(deviceData.device_mac || "").trim();

    if (!deviceName || !deviceModel || !deviceFirmware) {
      setSaveStatus({
        show: true,
        success: false,
        message: "Please fill in all required fields.",
      });
      return;
    }

    if (!deviceIp && !deviceMac) {
      setSaveStatus({
        show: true,
        success: false,
        message: "Please enter either an IP address or a MAC address.",
      });
      return;
    }

    if (deviceMac && !isValidMacAddress(deviceMac)) {
      setSaveStatus({
        show: true,
        success: false,
        message: "Please enter a valid MAC address, for example 7C:1E:B3:07:10:4A.",
      });
      return;
    }

    setSaving(true);
    setSaveStatus({ show: false, success: false, message: "" });
    setQuickSetupStatus({ show: false, success: false, message: "" });

    try {
      const site = getSite();
      const user = getUser();

      const payloadSupportsDeviceButton = ["2N IP Verso", "2N IP Solo"].includes(deviceModel);
      const payloadSupportsMultipleQuickDialButtons = deviceModel === "2N IP Verso";

      const quickDialButtonCount = payloadSupportsMultipleQuickDialButtons
        ? clampQuickDialButtonCount(deviceData.quick_dial_button_count)
        : 1;

      const deviceButtonAssignments = payloadSupportsDeviceButton
        ? buildButtonAssignmentState(
          quickDialButtonCount,
          deviceData.device_button_assignments || {}
        )
        : {};

      for (const [buttonPosition, tenantUuids] of Object.entries(deviceButtonAssignments)) {
        if ((tenantUuids || []).length > MAX_TENANTS_PER_DEVICE_BUTTON) {
          setSaveStatus({
            show: true,
            success: false,
            message: `Button ${buttonPosition} can have at most ${MAX_TENANTS_PER_DEVICE_BUTTON} tenants.`,
          });
          return;
        }
      }

      const payload = {
        ...deviceData,
        device_name: deviceName,
        device_model: deviceModel,
        device_firmware: deviceFirmware,
        site_uuid: deviceData.site_uuid || site.site_uuid,
        device_ip: deviceIp || null,
        device_mac: deviceMac || null,
        quick_dial_button_count: quickDialButtonCount,
        device_button_assignments: deviceButtonAssignments,

        // button 1 assignments still sent as device_button.
        device_button: payloadSupportsDeviceButton
          ? (deviceButtonAssignments["1"] || [])
          : [],

        extension_uuid: deviceData.extension_uuid || null,
      };

      const endpoint = editing
        ? `/hrms/update_device/${device_uuid}`
        : "/hrms/create_device";
      const method = editing ? "PUT" : "POST";

      const res = await fetch(`${serverHost}${endpoint}`, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ device: payload }),
      });

      const result = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(result.error || "Failed to save device");

      if (editing) {
        await saveDirectoryDisplaySettingsIfChanged();
      }

      // Apply display setting changes only when editing an existing supported device
      if (editing && deviceData.device_model === "2N IP Style") {
        await saveRfidBacklightIfChanged();
        await saveStyleDisplaySettingsIfChanged();
        await saveShowcaseModeIfChanged();
        await saveRootFolderDisplayIfChanged();
      }

      if (editing && deviceData.device_model === "2N IP Verso") {
        await saveVersoDisplaySettingsIfChanged();
      }
      setTimeout(() => {
        navigate("/admin/hrms/devices", { replace: true });
      }, 400);
    } catch (err) {
      console.error("Save device error:", err);

      setSaveStatus({
        show: true,
        success: false,
        message: err.message || "Error saving device",
      });

      setSaving(false);
    }
  };

  const loadEvents = async () => {
    if (!device_uuid) return;

    const requestId = Date.now();
    latestEventsRequestRef.current = requestId;

    try {
      setEventsLoading(true);
      setEventsError("");

      const subscribeResult = await subscribeToDeviceEvents(device_uuid, {
        filter: "CallStateChanged,CallSessionStateChanged,RegistrationStateChanged,UserAuthenticated,UserRejected,CodeEntered",
        include: "all",
        duration: 300,
      });

      const subscriptionId = subscribeResult.subscription_id;
      if (!subscriptionId) {
        throw new Error("No subscription id returned");
      }

      const pullResult = await pullDeviceEvents(device_uuid, {
        subscriptionId,
        timeout: 0,
        range: eventsRange,
        max_batches: 100,
      });

      if (latestEventsRequestRef.current !== requestId) {
        return;
      }

      const newEvents = pullResult.events || [];
      const seen = new Set();
      const deduped = [];

      for (const item of newEvents) {
        const key =
          item?.id ??
          `${item?.event}-${item?.utc_time}-${JSON.stringify(item?.params || {})}`;

        if (seen.has(key)) continue;
        seen.add(key);
        deduped.push(item);
      }

      deduped.sort((a, b) => getEventSortTime(b) - getEventSortTime(a));
      setEvents(deduped);
    } catch (error) {
      if (latestEventsRequestRef.current !== requestId) {
        return;
      }

      setEventsError(error.message || "Failed to load events");
      setEvents([]);
    } finally {
      if (latestEventsRequestRef.current === requestId) {
        setEventsLoading(false);
      }
    }
  };

  const loadCallLogs = async () => {
    if (!device_uuid) return;

    try {
      setCallLogsLoading(true);
      setCallLogsError("");

      const result = await getDeviceCallLogs(device_uuid, {
        limit: 300,
        range: callLogsRange,
      });

      setCallLogs(result.call_logs || []);
    } catch (error) {
      setCallLogsError(error.message || "Failed to load call logs");
    } finally {
      setCallLogsLoading(false);
    }
  };

  const formatCallLogDate = (value) => {
    if (!value) {
      return { date: "—", time: "" };
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return { date: value, time: "" };
    }

    const pad = (n) => String(n).padStart(2, "0");

    const date = `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
    const time = `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}:${pad(parsed.getSeconds())}`;

    return { date, time };
  };

  const formatCallLogDuration = (value) => {
    if (value === null || value === undefined || value === "") return "0s";

    const seconds = Number(value);
    if (Number.isNaN(seconds)) return String(value);

    if (seconds < 60) return `${seconds}s`;

    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    if (mins < 60) {
      return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
    }

    const hours = Math.floor(mins / 60);
    const remMins = mins % 60;

    if (remMins > 0) return `${hours}h ${remMins}m`;
    return `${hours}h`;
  };

  const formatCallLogName = (log) => {
    const candidates = [
      log?.name,
      log?.display_name,
      log?.remote_party,
      log?.sip_uri,
    ];

    for (const candidate of candidates) {
      if (!candidate) continue;

      let text = String(candidate).trim();
      if (!text) continue;

      if (text.toLowerCase().startsWith("sip:")) {
        text = text.slice(4);
      }

      return text;
    }

    return "—";
  };

  const getCallTypeDisplay = (type) => {
    if (!type) return "Call";

    const normalized = String(type).toLowerCase();

    if (normalized === "incoming") return "Incoming";
    if (normalized === "outgoing") return "Outgoing";
    if (normalized === "missed") return "Missed";

    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const formatEventTime = (evt) => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    let dateObj = null;

    if (typeof evt?.utc_time === "number") {
      dateObj = new Date(evt.utc_time * 1000);
    } else if (typeof evt?.local_time_unix === "number") {
      dateObj = new Date(evt.local_time_unix * 1000);
    }

    if (!dateObj || Number.isNaN(dateObj.getTime())) {
      return "—";
    }

    const pad = (n) => String(n).padStart(2, "0");

    return `${pad(dateObj.getDate())} ${months[dateObj.getMonth()]} ${pad(dateObj.getHours())}:${pad(dateObj.getMinutes())}:${pad(dateObj.getSeconds())}`;
  };

  const formatEventType = (evt) => {
    return evt?.event || "—";
  };

  const formatEventDescription = (evt) => {
    const params = evt?.params || {};

    const parts = [];

    const orderedKeys = [
      "sipAccountType",
      "sipAccount",
      "account",
      "direction",
      "state",
      "reason",
      "peer",
      "session",
      "call",
      "originator",
      "info",
      "name",
      "uuid",
      "code",
      "valid",
    ];

    orderedKeys.forEach((key) => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== "") {
        parts.push(`${key}=${params[key]}`);
      }
    });

    Object.keys(params).forEach((key) => {
      if (orderedKeys.includes(key)) return;
      const value = params[key];
      if (value !== undefined && value !== null && value !== "") {
        parts.push(`${key}=${value}`);
      }
    });

    return parts.length ? parts.join(", ") : "—";
  };

  const getRangeCutoffDate = (range) => {
    if (range === "ALL") return null;

    const now = new Date();
    const cutoff = new Date(now);

    if (range === "1D") {
      cutoff.setDate(now.getDate() - 1);
    } else if (range === "3D") {
      cutoff.setDate(now.getDate() - 3);
    } else if (range === "7D") {
      cutoff.setDate(now.getDate() - 7);
    } else {
      return null;
    }

    return cutoff;
  };

  const isWithinRange = (dateValue, range) => {
    if (!dateValue || range === "ALL") return true;

    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return true;

    const cutoff = getRangeCutoffDate(range);
    if (!cutoff) return true;

    return parsed >= cutoff;
  };

  const filteredCallLogs = callLogs.filter((log) => {
    return isWithinRange(log?.date, callLogsRange);
  });

  const isEventWithinRange = (evt, range) => {
    if (range === "ALL") return true;

    let eventDate = null;

    if (evt?.local_time_unix) {
      eventDate = new Date(evt.local_time_unix * 1000);
    } else if (evt?.utc_time) {
      eventDate = new Date(evt.utc_time * 1000);
    }

    if (!eventDate || Number.isNaN(eventDate.getTime())) return true;

    const cutoff = getRangeCutoffDate(range);
    if (!cutoff) return true;

    return eventDate >= cutoff;
  };

  const getEventSortTime = (evt) => {
    if (typeof evt?.utc_time === "number") return evt.utc_time;
    if (typeof evt?.local_time_unix === "number") return evt.local_time_unix;

    const fallback = evt?.id;
    if (typeof fallback === "number") return fallback;

    const parsed = Number(fallback);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const filteredEvents = [...events]
    .filter((evt) => {
      const matchesType = eventFilter === "ALL" ? true : evt?.event === eventFilter;
      const matchesRange = isEventWithinRange(evt, eventsRange);
      return matchesType && matchesRange;
    })
    .sort((a, b) => getEventSortTime(b) - getEventSortTime(a));

  const availableEventTypes = Array.from(
    new Set((events || []).map((evt) => evt?.event).filter(Boolean))
  );

  const isNewDevice = !editing;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <div className="text-center">
          <div className="spinner-border mb-3" role="status" />
          <div>Loading device…</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SimpleHeader name={isNewDevice ? "New Device" : "Edit Device"} parentName="HRMS" />
      <Container className="mt--6" fluid>
        {saveStatus.show && (
          <Alert
            color={saveStatus.success ? "success" : "danger"}
            isOpen={saveStatus.show}
            toggle={() => setSaveStatus({ ...saveStatus, show: false })}
          >
            {saveStatus.message}
          </Alert>
        )}
        <Row>
          <Col className="order-xl-1" xl="12">
            <Card>
              <CardHeader
                style={{
                  backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                  color: isDark ? "#e2e8f0" : "#111",
                }}
              >
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3
                      className="mb-0"
                      style={{ color: isDark ? "#e2e8f0" : "#111" }}
                    >
                      {isNewDevice ? "Add New Device" : "Edit Device"}
                    </h3>
                  </Col>
                  <Col xs="4" className="d-flex justify-content-end">
                    <Button color="danger" onClick={() => navigate('/admin/hrms/devices')} size="sm" disabled={isBusy}>
                      Back to Enterphones
                    </Button>
                    <Button color="success" onClick={handleSave} size="sm" disabled={isBusy}>
                      Save
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <Form>
                  {/* Device Information */}
                  <div className="pl-lg-4">
                    <h6 className="heading-small mb-4" style={sectionHeadingStyle}>
                      Device Information
                    </h6>
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-device_name"
                            style={labelStyle}
                          >
                            Device Name <span className="text-danger">*</span>
                          </label>
                          <Input
                            id="input-device_name"
                            value={deviceData.device_name}
                            disabled={isBusy}
                            onChange={handleInputChange}
                            placeholder="Enter device name"
                            type="text"
                            required
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-device_model"
                            style={labelStyle}
                          >
                            Device Model <span className="text-danger">*</span>
                          </label>
                          <Input
                            id="input-device_model"
                            type="select"
                            disabled={isBusy}
                            value={deviceData.device_model}
                            onChange={handleInputChange}
                            required
                          >
                            <option value="">Select Model</option>
                            <option value="2N IP Style">2N IP Style</option>
                            <option value="2N IP Verso">2N IP Verso</option>
                            <option value="2N IP Solo">2N IP Solo</option>
                            <option value={AKUVOX_S539_MODEL}>{AKUVOX_S539_MODEL}</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="4">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-device_ip"
                            style={labelStyle}
                          >
                            IP Address
                          </label>
                          <Input
                            id="input-device_ip"
                            value={deviceData.device_ip}
                            disabled={isBusy}
                            onChange={handleInputChange}
                            placeholder="Enter IP address"
                            type="text"
                          />
                          <small className="form-text" style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                            Required if MAC address is blank. Last known IP can be updated by discovery.
                          </small>
                        </FormGroup>
                      </Col>

                      <Col lg="4">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-device_mac"
                            style={labelStyle}
                          >
                            MAC Address
                          </label>
                          <Input
                            id="input-device_mac"
                            value={deviceData.device_mac}
                            disabled={isBusy}
                            onChange={handleInputChange}
                            placeholder="AA:BB:CC:DD:EE:FF"
                            type="text"
                          />
                          <small className="form-text" style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                            Required if IP address is blank. Used to rediscover the device if its IP changes.
                          </small>
                        </FormGroup>
                      </Col>

                      <Col lg="4">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-device_firmware"
                            style={labelStyle}
                          >
                            Firmware Version <span className="text-danger">*</span>
                          </label>
                          <Input
                            id="input-device_firmware"
                            type="select"
                            disabled={isBusy}
                            value={deviceData.device_firmware}
                            onChange={handleInputChange}
                            required
                          >
                            {isAkuvoxS539 ? (
                              <option value="539.30.10.130">539.30.10.130</option>
                            ) : (
                              <option value="2.46">2.46</option>
                            )}
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label" style={labelStyle}>
                            Directories
                          </label>

                          <HusariaSelect
                            styles={selectStyles}
                            isDisabled={isBusy}
                            isMulti
                            options={directories.map(d => ({
                              value: d.dir_uuid,
                              label: d.dir_name
                            }))}
                            value={(deviceData.dir_uuids || [])
                              .map(id => {
                                const dir = directories.find(d => d.dir_uuid === id);
                                return dir
                                  ? {
                                    value: dir.dir_uuid,
                                    label: dir.dir_name
                                  }
                                  : null;
                              })
                              .filter(Boolean)}
                            onChange={(selectedOptions) => {
                              const selectedIds = (selectedOptions || []).map(opt => opt.value);
                              setDeviceData(prev => ({
                                ...prev,
                                dir_uuids: selectedIds
                              }));
                            }}
                            placeholder="Select directories..."
                          />
                        </FormGroup>
                      </Col>

                      {supportsDeviceButton && (
                        <Col lg="12">
                          <div
                            style={{
                              marginTop: "1rem",
                              padding: "1rem",
                              border: isDark ? "1px solid #374151" : "1px solid #e9ecef",
                              borderRadius: "0.5rem",
                              backgroundColor: isDark ? "#111827" : "#f8f9fa",
                            }}
                          >
                            <Row>
                              {supportsMultipleQuickDialButtons ? (
                                <>
                                  <Col lg="4">
                                    <FormGroup>
                                      <label
                                        className="form-control-label"
                                        htmlFor="input-quick_dial_button_count"
                                        style={labelStyle}
                                      >
                                        Number of Quick Dial Buttons
                                      </label>

                                      <Input
                                        id="input-quick_dial_button_count"
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        disabled={isBusy}
                                        value={deviceData.quick_dial_button_count ?? ""}
                                        onChange={handleQuickDialButtonCountChange}
                                        onBlur={handleQuickDialButtonCountBlur}
                                        placeholder="Enter button count"
                                      />

                                      <small
                                        className="form-text"
                                        style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
                                      >
                                        Assign tenants to each button position.
                                      </small>
                                    </FormGroup>
                                  </Col>

                                </>
                              ) : (
                                <Col lg="12">
                                  <div
                                    style={{
                                      color: isDark ? "#d1d5db" : "#495057",
                                      fontSize: "0.875rem",
                                      marginBottom: "1rem",
                                    }}
                                  >
                                    Assign tenants to Button 1 below.
                                  </div>
                                </Col>
                              )}
                            </Row>

                            {deviceData.dir_uuids.length === 0 ? (
                              <Alert color="info" className="mb-0">
                                Select at least one directory before assigning tenants to quick dial buttons.
                              </Alert>
                            ) : (
                              <Row>
                                {Array.from({
                                  length: getEffectiveQuickDialButtonCount(),
                                }).map((_, index) => {
                                  const buttonNumber = index + 1;

                                  return (
                                    <Col lg="6" key={`quick-dial-button-${buttonNumber}`}>
                                      <FormGroup>
                                        <label
                                          className="form-control-label"
                                          style={labelStyle}
                                        >
                                          Button {buttonNumber}
                                        </label>

                                        {(() => {
                                          const selectedTenants = getSelectedTenantsForButton(buttonNumber);

                                          return (
                                            <div
                                              style={{
                                                border: isDark ? "1px solid #374151" : "1px solid #dee2e6",
                                                borderRadius: "0.5rem",
                                                backgroundColor: isDark ? "#1f2937" : "#ffffff",
                                                padding: "0.75rem",
                                              }}
                                            >
                                              <div className="d-flex justify-content-between align-items-center mb-2">
                                                <div
                                                  style={{
                                                    color: isDark ? "#d1d5db" : "#495057",
                                                    fontSize: "0.875rem",
                                                  }}
                                                >
                                                  {selectedTenants.length === 0
                                                    ? "No tenants assigned"
                                                    : `${selectedTenants.length} tenant${selectedTenants.length === 1 ? "" : "s"} assigned`}
                                                </div>

                                                <Button
                                                  color="primary"
                                                  size="sm"
                                                  type="button"
                                                  disabled={isBusy || directoryTenants.length === 0}
                                                  onClick={() => openTenantPicker(buttonNumber)}
                                                >
                                                  Add User
                                                </Button>
                                              </div>

                                              {selectedTenants.length > 0 && (
                                                <div style={{ overflowX: "auto" }}>
                                                  <Table
                                                    size="sm"
                                                    className="mb-0"
                                                    style={{
                                                      color: isDark ? "#f8f9fa" : "#111",
                                                      backgroundColor: "transparent",
                                                    }}
                                                  >
                                                    <thead>
                                                      <tr>
                                                        <th>Name</th>
                                                        <th>Virtual Number</th>
                                                        <th>Phone 1</th>
                                                        <th style={{ width: "80px" }} />
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {selectedTenants.map((tenant) => (
                                                        <tr key={`button-${buttonNumber}-tenant-${tenant.tenant_uuid}`}>
                                                          <td>{getTenantName(tenant)}</td>
                                                          <td>{getTenantVirtualNumber(tenant) || "—"}</td>
                                                          <td>{getTenantPhone1(tenant) || "—"}</td>
                                                          <td className="text-right">
                                                            <Button
                                                              color="danger"
                                                              size="sm"
                                                              type="button"
                                                              disabled={isBusy}
                                                              onClick={() =>
                                                                removeTenantFromButton(buttonNumber, tenant.tenant_uuid)
                                                              }
                                                              style={
                                                                isBusy
                                                                  ? removeTenantButtonDisabledStyle
                                                                  : removeTenantButtonStyle
                                                              }
                                                            >
                                                              Remove
                                                            </Button>
                                                          </td>
                                                        </tr>
                                                      ))}
                                                    </tbody>
                                                  </Table>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })()}
                                      </FormGroup>
                                    </Col>
                                  );
                                })}
                              </Row>
                            )}
                          </div>
                        </Col>
                      )}
                    </Row>
                  </div>

                  {/* SIP Configuration */}
                  <div className="pl-lg-4 mt-4">
                    <Row>
                      <Col lg="12">
                        <h6 className="heading-small mb-4" style={sectionHeadingStyle}>
                          SIP Configuration
                        </h6>

                        <FormGroup className="mb-0">
                          <label
                            className="form-control-label"
                            htmlFor="input-extension_uuid"
                            style={labelStyle}
                          >
                            Extension
                          </label>
                          <HusariaSelect
                            inputId="input-extension_uuid"
                            styles={selectStyles}
                            options={extensionOptions.map((opt) => ({
                              value: opt.extension_uuid,
                              label: opt.label,
                            }))}
                            value={
                              extensionOptions
                                .map((opt) => ({
                                  value: opt.extension_uuid,
                                  label: opt.label,
                                }))
                                .find((opt) => opt.value === deviceData.extension_uuid) || null
                            }
                            onChange={(selected) =>
                              setDeviceData((prev) => ({
                                ...prev,
                                extension_uuid: selected?.value || "",
                              }))
                            }
                            isDisabled={isBusy}
                            isClearable
                            placeholder="Select extension..."
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>

                  <hr className="my-4" />

                  {/* Display Settings */}
                  {editing && deviceData.device_model === "2N IP Style" && (
                    <div className="pl-lg-4 mt-4">
                      <Card
                        className="shadow-sm"
                        style={{
                          border: isDark ? "1px solid #3a3a3a" : "1px solid #dcdfe3",
                          boxShadow: "none",
                        }}
                      >
                        <CardHeader
                          onClick={() => setDisplaySettingsOpen((prev) => !prev)}
                          style={{
                            ...collapsibleHeaderStyle,
                            borderBottom: displaySettingsOpen
                              ? isDark ? "1px solid #374151" : "1px solid #dcdfe3"
                              : "none",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h4
                                className="mb-1"
                                style={{
                                  color: isDark ? "#f8f9fa" : "#111",
                                  fontWeight: 600,
                                  fontSize: "1.35rem",
                                }}
                              >
                                Display Settings
                              </h4>
                              <div
                                style={{
                                  color: isDark ? "#9ca3af" : "#6c757d",
                                  fontSize: "0.95rem",
                                }}
                              >
                                Screen display, code entry, touch sounds, background image, directory images, & visual options
                              </div>
                            </div>

                            {renderCollapseIcon(displaySettingsOpen)}
                          </div>
                        </CardHeader>

                        {displaySettingsOpen && (
                          <CardBody
                            style={{
                              backgroundColor: isDark ? "#111827" : "#ffffff",
                              paddingTop: "1.25rem",
                            }}
                          >
                            {quickSetupStatus.show &&
                              !isRootFolderUnsupportedMessage(quickSetupStatus.message) && (
                                <Alert
                                  color={quickSetupStatus.success ? "success" : "danger"}
                                  isOpen={quickSetupStatus.show}
                                  toggle={() =>
                                    setQuickSetupStatus((prev) => ({ ...prev, show: false }))
                                  }
                                  className="mb-4"
                                >
                                  {quickSetupStatus.message}
                                </Alert>
                              )}
                            {backgroundStatus.show && (
                              <Alert
                                color={backgroundStatus.success ? "success" : "danger"}
                                isOpen={backgroundStatus.show}
                                toggle={() =>
                                  setBackgroundStatus((prev) => ({ ...prev, show: false }))
                                }
                                className="mb-4"
                              >
                                {backgroundStatus.message}
                              </Alert>
                            )}

                            <h6 className="heading-small mb-3" style={sectionHeadingStyle}>
                              Screen Options
                            </h6>

                            <Card
                              className="mb-4"
                              style={{
                                backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
                                border: isDark ? "1px solid #374151" : "1px solid #e9ecef",
                                boxShadow: "none",
                              }}
                            >
                              <CardBody>
                                {[
                                  {
                                    id: "rfid-backlight-toggle",
                                    label: "RFID Symbol Backlight",
                                    description: "Toggle the backlight for the RFID symbol.",
                                    checked: rfidBacklightEnabled,
                                    onChange: handleRfidBacklightToggle,
                                    loading: rfidBacklightLoading,
                                  },
                                  {
                                    id: "style-flexi-builder-toggle",
                                    label: "Flexi Builder / Custom GUI",
                                    description:
                                      "Switches the IP Style between the standard 2N display UI and the custom Flexi GUI layout. Only enable this if the device already has a valid Flexi layout configured.",
                                    checked: !!styleDisplaySettings.flexiBuilderEnabled,
                                    onChange: handleStyleDisplayToggle("flexiBuilderEnabled"),
                                    loading: styleDisplaySettingsLoading,
                                  },
                                  {
                                    id: "style-code-entry-button-toggle",
                                    label: "Code Entry Button",
                                    description: "Shows or hides the code entry button on the device display.",
                                    checked: !!styleDisplaySettings.codeEntryButton,
                                    onChange: handleStyleDisplayToggle("codeEntryButton"),
                                    loading: styleDisplaySettingsLoading,
                                  },
                                  {
                                    id: "style-touch-sounds-toggle",
                                    label: "Touch Sounds",
                                    description: "Enables or disables acoustic feedback when the display is touched.",
                                    checked: !!styleDisplaySettings.touchSounds,
                                    onChange: handleStyleDisplayToggle("touchSounds"),
                                    loading: styleDisplaySettingsLoading,
                                  },
                                ].map((item) => (
                                  <div
                                    key={item.id}
                                    className="d-flex justify-content-between align-items-start"
                                    style={{
                                      padding: "0.5rem 0",
                                      gap: "1rem",
                                      opacity: item.loading ? 0.7 : 1,
                                    }}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <div
                                        className="d-flex align-items-center"
                                        style={{
                                          color: isDark ? "#f8f9fa" : "#111",
                                          fontWeight: 600,
                                          marginBottom: "0.2rem",
                                          gap: "0.5rem",
                                        }}
                                      >
                                        <span>{item.label}</span>
                                        {item.loading && (
                                          <small style={{ color: isDark ? "#9ca3af" : "#6c757d", fontWeight: 500 }}>
                                            Loading...
                                          </small>
                                        )}
                                      </div>

                                      <small className="form-text mt-0" style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                                        {item.description}
                                      </small>
                                    </div>

                                    <div className="custom-control custom-switch ml-3" style={{ paddingTop: "0.15rem" }}>
                                      <input
                                        type="checkbox"
                                        className="custom-control-input"
                                        id={item.id}
                                        checked={item.checked}
                                        onChange={item.onChange}
                                        disabled={isBusy || item.loading}
                                        style={{
                                          cursor: (isBusy || item.loading) ? "not-allowed" : "pointer",
                                        }}
                                      />

                                      <label
                                        className="custom-control-label"
                                        htmlFor={item.id}
                                        style={{
                                          color: isDark ? "#f8f9fa" : "#111",
                                          cursor: (isBusy || item.loading) ? "not-allowed" : "pointer",
                                          minWidth: "32px",
                                        }}
                                      >
                                        {item.checked ? "On" : "Off"}
                                      </label>
                                    </div>
                                  </div>
                                ))}

                                <hr style={{ borderColor: isDark ? "#374151" : "#e9ecef", margin: "0.75rem 0" }} />

                                <div
                                  style={{
                                    padding: "0.25rem 0 0.5rem 0",
                                    opacity: styleDisplaySettingsLoading ? 0.7 : 1,
                                  }}
                                >
                                  <div className="d-flex align-items-center" style={{ gap: "0.5rem", marginBottom: "0.35rem" }}>
                                    <div style={{ color: isDark ? "#f8f9fa" : "#111", fontWeight: 600 }}>
                                      Code Entry Keypad Mode
                                    </div>

                                    {styleDisplaySettingsLoading && (
                                      <small style={{ color: isDark ? "#9ca3af" : "#6c757d", fontWeight: 500 }}>
                                        Loading...
                                      </small>
                                    )}
                                  </div>

                                  <small className="form-text mt-0 mb-2" style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                                    Normal keeps the keypad ordered. Scrambled randomizes the keypad layout.
                                  </small>

                                  <Input
                                    id="input-style-entry-keypad-mode"
                                    type="select"
                                    value={styleDisplaySettings.entryKeypadMode}
                                    onChange={handleStyleKeypadModeChange}
                                    disabled={isBusy || styleDisplaySettingsLoading}
                                    style={{
                                      cursor: (isBusy || styleDisplaySettingsLoading) ? "not-allowed" : "pointer",
                                      backgroundColor: isDark ? "#2a2a2a" : "#fff",
                                      color: isDark ? "#f8f9fa" : "#111",
                                      borderColor: isDark ? "#3a3a3a" : undefined,
                                    }}
                                  >
                                    <option value="NORMAL">Normal</option>
                                    <option value="SCRAMBLED">Scrambled</option>
                                  </Input>
                                </div>

                                <hr style={{ borderColor: isDark ? "#374151" : "#e9ecef", margin: "0.75rem 0" }} />

                                <div
                                  style={{
                                    padding: "0.25rem 0 0.5rem 0",
                                    opacity: rootFolderDisplayLoading ? 0.7 : 1,
                                  }}
                                >
                                  <div className="d-flex align-items-center" style={{ gap: "0.5rem", marginBottom: "0.35rem" }}>
                                    <div style={{ color: isDark ? "#f8f9fa" : "#111", fontWeight: 600 }}>
                                      Directories Display
                                    </div>

                                    {rootFolderDisplayLoading && (
                                      <small style={{ color: isDark ? "#9ca3af" : "#6c757d", fontWeight: 500 }}>
                                        Loading...
                                      </small>
                                    )}
                                  </div>

                                  <small className="form-text mt-0 mb-2" style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                                    Choose how the directories are displayed on the device.
                                  </small>

                                  <Input
                                    id="input-root-folder-display"
                                    type="select"
                                    value={rootFolderDisplay}
                                    onChange={handleRootFolderDisplayChange}
                                    disabled={isBusy || rootFolderDisplayLoading}
                                    style={{
                                      cursor: (isBusy || rootFolderDisplayLoading) ? "not-allowed" : "pointer",
                                      backgroundColor: isDark ? "#2a2a2a" : "#fff",
                                      color: isDark ? "#f8f9fa" : "#111",
                                      borderColor: isDark ? "#3a3a3a" : undefined,
                                    }}
                                  >
                                    <option value="CARDS_1_COLUMN">1 column</option>
                                    <option value="CARDS_2_COLUMNS">2 columns</option>
                                    <option value="PHONEBOOK">Phonebook</option>
                                  </Input>
                                </div>

                                <hr style={{ borderColor: isDark ? "#374151" : "#e9ecef", margin: "0.75rem 0" }} />

                                <div
                                  style={{
                                    padding: "0.25rem 0 0.5rem 0",
                                    opacity: showcaseModeLoading ? 0.7 : 1,
                                  }}
                                >
                                  <div className="d-flex align-items-center" style={{ gap: "0.5rem", marginBottom: "0.35rem" }}>
                                    <div style={{ color: isDark ? "#f8f9fa" : "#111", fontWeight: 600 }}>
                                      Showcase Mode
                                    </div>

                                    {showcaseModeLoading && (
                                      <small style={{ color: isDark ? "#9ca3af" : "#6c757d", fontWeight: 500 }}>
                                        Loading...
                                      </small>
                                    )}
                                  </div>

                                  <small className="form-text mt-0 mb-2" style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                                    Choose what the device shows on the screen in showcase mode.
                                  </small>

                                  <Input
                                    id="input-showcase-mode"
                                    type="select"
                                    value={showcaseMode}
                                    onChange={handleShowcaseModeChange}
                                    disabled={isBusy || showcaseModeLoading}
                                    style={{
                                      cursor: (isBusy || showcaseModeLoading) ? "not-allowed" : "pointer",
                                      backgroundColor: isDark ? "#2a2a2a" : "#fff",
                                      color: isDark ? "#f8f9fa" : "#111",
                                      borderColor: isDark ? "#3a3a3a" : undefined,
                                    }}
                                  >
                                    <option value="OFF">Off</option>
                                    <option value="SLIDESHOW">Slideshow</option>
                                    <option value="LOGO">Logo</option>
                                    <option value="ADDRESS">Address</option>
                                    <option value="DATE_TIME">Date & Time</option>
                                  </Input>
                                </div>
                              </CardBody>
                            </Card>

                            <h6 className="heading-small mb-3" style={sectionHeadingStyle}>
                              Background
                            </h6>

                            <Card
                              className="mb-4"
                              style={{
                                backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
                                border: isDark ? "1px solid #374151" : "1px solid #e9ecef",
                                boxShadow: "none",
                              }}
                            >
                              <CardBody>
                                <div
                                  style={{
                                    backgroundColor: isDark ? "#111827" : "#ffffff",
                                    border: isDark ? "1px solid #374151" : "1px solid #e5e7eb",
                                    borderRadius: "0.75rem",
                                    padding: "1rem",
                                    boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
                                  }}
                                >
                                  <div
                                    className="d-flex justify-content-between align-items-start"
                                    style={{ gap: "1rem", marginBottom: "0.9rem" }}
                                  >
                                    <div style={{ minWidth: 0 }}>
                                      <div
                                        style={{
                                          color: isDark ? "#f8f9fa" : "#111827",
                                          fontWeight: 700,
                                          fontSize: "1rem",
                                          lineHeight: 1.2,
                                        }}
                                      >
                                        Background Image
                                      </div>

                                      <div
                                        style={{
                                          color: isDark ? "#9ca3af" : "#6b7280",
                                          fontSize: "0.82rem",
                                          marginTop: "0.2rem",
                                        }}
                                      >
                                        Main display background for the device
                                      </div>
                                    </div>

                                    {backgroundFile && (
                                      <div
                                        style={{
                                          color: backgroundMeta.validDimensions
                                            ? isDark ? "#86efac" : "#198754"
                                            : isDark ? "#fca5a5" : "#dc3545",
                                          backgroundColor: backgroundMeta.validDimensions
                                            ? isDark ? "rgba(22, 163, 74, 0.15)" : "#e8f5ee"
                                            : isDark ? "rgba(220, 38, 38, 0.15)" : "#fdecec",
                                          border: backgroundMeta.validDimensions
                                            ? isDark ? "1px solid rgba(134, 239, 172, 0.35)" : "1px solid #badbcc"
                                            : isDark ? "1px solid rgba(252, 165, 165, 0.35)" : "1px solid #f5c2c7",
                                          borderRadius: "999px",
                                          padding: "0.25rem 0.65rem",
                                          fontSize: "0.78rem",
                                          fontWeight: 700,
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {backgroundMeta.validDimensions ? "Ready to upload" : "Invalid size"}
                                      </div>
                                    )}
                                  </div>

                                  <div
                                    className="d-flex align-items-start"
                                    style={{ gap: "0.9rem" }}
                                  >
                                    <div
                                      style={{
                                        width: "96px",
                                        height: "154px",
                                        minWidth: "96px",
                                        borderRadius: "0.75rem",
                                        overflow: "hidden",
                                        backgroundColor: isDark ? "#1f2937" : "#f3f4f6",
                                        border: isDark ? "1px solid #374151" : "1px solid #d1d5db",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      {backgroundPreview ? (
                                        <img
                                          src={backgroundPreview}
                                          alt="Background preview"
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            display: "block",
                                          }}
                                        />
                                      ) : (
                                        <div
                                          style={{
                                            color: isDark ? "#9ca3af" : "#6b7280",
                                            fontSize: "0.75rem",
                                            textAlign: "center",
                                            padding: "0.35rem",
                                            lineHeight: 1.2,
                                          }}
                                        >
                                          No image
                                        </div>
                                      )}
                                    </div>

                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <Input
                                        id="input-device-background"
                                        type="file"
                                        accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                                        disabled={isBusy || backgroundUploading}
                                        onChange={handleBackgroundFileChange}
                                        style={{ display: "none" }}
                                      />

                                      <div
                                        className="d-flex align-items-center flex-wrap"
                                        style={{ gap: "0.5rem", marginBottom: "1.25rem" }}
                                      >
                                        <Button
                                          type="button"
                                          size="sm"
                                          color={isDark ? undefined : "secondary"}
                                          disabled={isBusy || backgroundUploading}
                                          style={{
                                            backgroundColor: isDark ? "#374151" : undefined,
                                            borderColor: isDark ? "#4b5563" : undefined,
                                            color: isDark ? "#f8f9fa" : undefined,
                                            fontWeight: 600,
                                          }}
                                          onClick={() => document.getElementById("input-device-background")?.click()}
                                        >
                                          {backgroundPreview ? "Change Image" : "Choose Image"}
                                        </Button>

                                        {backgroundFile && (
                                          <Button
                                            type="button"
                                            size="sm"
                                            color={isDark ? undefined : "secondary"}
                                            disabled={isBusy || backgroundUploading}
                                            style={{
                                              backgroundColor: isDark ? "#374151" : undefined,
                                              borderColor: isDark ? "#4b5563" : undefined,
                                              color: isDark ? "#f8f9fa" : undefined,
                                              fontWeight: 600,
                                            }}
                                            onClick={clearPendingBackgroundSelection}
                                          >
                                            Cancel Selection
                                          </Button>
                                        )}

                                        {savedBackgroundPreview && !backgroundFile && (
                                          <Button
                                            type="button"
                                            size="sm"
                                            color="danger"
                                            disabled={isBusy}
                                            style={{ fontWeight: 600 }}
                                            onClick={handleRemoveBackground}
                                          >
                                            {backgroundRemoving ? "Removing..." : "Remove Image"}
                                          </Button>
                                        )}

                                        {backgroundFile && (
                                          <Button
                                            type="button"
                                            size="sm"
                                            color="primary"
                                            onClick={handleBackgroundUpload}
                                            disabled={isBusy || !backgroundMeta.validDimensions}
                                            style={{ fontWeight: 600 }}
                                          >
                                            {backgroundUploading ? "Uploading..." : "Upload Background"}
                                          </Button>
                                        )}
                                      </div>

                                      <div
                                        style={{
                                          color: isDark ? "#d1d5db" : "#374151",
                                          fontSize: "0.78rem",
                                          fontWeight: 700,
                                          textTransform: "uppercase",
                                          letterSpacing: "0.04em",
                                          marginBottom: "0.35rem",
                                        }}
                                      >
                                        Requirements
                                      </div>

                                      <div
                                        style={{
                                          color: isDark ? "#9ca3af" : "#6b7280",
                                          fontSize: "0.82rem",
                                          lineHeight: 1.45,
                                        }}
                                      >
                                        JPG or PNG. Minimum size: <strong>800 x 1280</strong>. Larger images will be reduced automatically.
                                      </div>

                                      {backgroundFile && (
                                        <div
                                          style={{
                                            marginTop: "0.75rem",
                                            padding: "0.75rem",
                                            borderRadius: "0.5rem",
                                            backgroundColor: isDark ? "#1f2937" : "#f9fafb",
                                            border: isDark ? "1px solid #374151" : "1px solid #e5e7eb",
                                          }}
                                        >
                                          <div
                                            style={{
                                              color: isDark ? "#f8f9fa" : "#111827",
                                              fontSize: "0.86rem",
                                              fontWeight: 600,
                                              whiteSpace: "nowrap",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                            }}
                                            title={backgroundFile.name}
                                          >
                                            {backgroundFile.name}
                                          </div>

                                          {backgroundMeta.width && backgroundMeta.height && (
                                            <div
                                              style={{
                                                color: backgroundMeta.validDimensions
                                                  ? isDark ? "#86efac" : "#198754"
                                                  : isDark ? "#fca5a5" : "#dc3545",
                                                fontSize: "0.82rem",
                                                marginTop: "0.25rem",
                                              }}
                                            >
                                              {backgroundMeta.width} x {backgroundMeta.height}
                                              {backgroundMeta.validDimensions ? " ✓" : " — must be at least 800 x 1280"}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </CardBody>
                            </Card>

                            {(deviceData.dir_uuids || []).length > 0 && (
                              <>
                                <h6 className="heading-small mb-3" style={sectionHeadingStyle}>
                                  Directory Display Settings
                                </h6>

                                <Card
                                  style={{
                                    backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
                                    border: isDark ? "1px solid #374151" : "1px solid #e9ecef",
                                    boxShadow: "none",
                                  }}
                                >
                                  <CardBody>
                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
                                        gap: "1rem",
                                      }}
                                    >
                                      {(deviceData.dir_uuids || []).map((dirUuid) => {
                                        const dir = directories.find((d) => d.dir_uuid === dirUuid);
                                        if (!dir) return null;

                                        const settings = directoryDisplaySettings[dirUuid] || {};
                                        const hasImage = !!settings.image;

                                        return (
                                          <div
                                            key={dirUuid}
                                            style={{
                                              backgroundColor: isDark ? "#111827" : "#ffffff",
                                              border: isDark ? "1px solid #374151" : "1px solid #e5e7eb",
                                              borderRadius: "0.75rem",
                                              padding: "1rem",
                                              boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
                                            }}
                                          >
                                            <div
                                              className="d-flex justify-content-between align-items-start"
                                              style={{ gap: "1rem", marginBottom: "0.9rem" }}
                                            >
                                              <div style={{ minWidth: 0 }}>
                                                <div
                                                  style={{
                                                    color: isDark ? "#f8f9fa" : "#111827",
                                                    fontWeight: 700,
                                                    fontSize: "1rem",
                                                    lineHeight: 1.2,
                                                    wordBreak: "break-word",
                                                  }}
                                                >
                                                  {dir.dir_name}
                                                </div>

                                                <div
                                                  style={{
                                                    color: isDark ? "#9ca3af" : "#6b7280",
                                                    fontSize: "0.82rem",
                                                    marginTop: "0.2rem",
                                                  }}
                                                >
                                                  Directory image and display order
                                                </div>
                                              </div>

                                              <Input
                                                type="select"
                                                title="Priority controls the directory order on the device. Priority 1 appears at the top of the list. No Priority appears at the bottom."
                                                value={settings.priority ?? ""}
                                                disabled={isBusy}
                                                onChange={(e) => {
                                                  const value = e.target.value;

                                                  setDirectoryDisplaySettings((prev) => ({
                                                    ...prev,
                                                    [dirUuid]: {
                                                      ...(prev[dirUuid] || {}),
                                                      priority: value,
                                                    },
                                                  }));
                                                }}
                                                style={{
                                                  width: "165px",
                                                  minWidth: "165px",
                                                  minHeight: "42px",
                                                  height: "42px",
                                                  cursor: isBusy ? "not-allowed" : "pointer",
                                                  backgroundColor: isDark ? "#1f2937" : "#fff",
                                                  color: isDark ? "#f8f9fa" : "#111",
                                                  borderColor: isDark ? "#374151" : "#d1d5db",
                                                  fontSize: "0.9rem",
                                                  lineHeight: "1.4",
                                                  paddingTop: "0.5rem",
                                                  paddingBottom: "0.5rem",
                                                  paddingLeft: "0.75rem",
                                                  paddingRight: "2rem",
                                                }}
                                              >
                                                <option value="">No Priority</option>
                                                <option value="1">★ 1</option>
                                                <option value="2">★ 2</option>
                                                <option value="3">★ 3</option>
                                                <option value="4">★ 4</option>
                                                <option value="5">★ 5</option>
                                                <option value="6">★ 6</option>
                                                <option value="7">★ 7</option>
                                              </Input>
                                            </div>

                                            <div
                                              className="d-flex align-items-start"
                                              style={{ gap: "0.9rem" }}
                                            >
                                              <div
                                                style={{
                                                  width: "76px",
                                                  height: "76px",
                                                  minWidth: "76px",
                                                  borderRadius: "0.75rem",
                                                  overflow: "hidden",
                                                  backgroundColor: isDark ? "#1f2937" : "#f3f4f6",
                                                  border: isDark ? "1px solid #374151" : "1px solid #d1d5db",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                }}
                                              >
                                                {hasImage ? (
                                                  <img
                                                    src={settings.image}
                                                    alt={`${dir.dir_name} preview`}
                                                    style={{
                                                      width: "100%",
                                                      height: "100%",
                                                      objectFit: "cover",
                                                      display: "block",
                                                    }}
                                                  />
                                                ) : (
                                                  <div
                                                    style={{
                                                      color: isDark ? "#9ca3af" : "#6b7280",
                                                      fontSize: "0.75rem",
                                                      textAlign: "center",
                                                      padding: "0.35rem",
                                                      lineHeight: 1.2,
                                                    }}
                                                  >
                                                    No image
                                                  </div>
                                                )}
                                              </div>

                                              <div style={{ flex: 1, minWidth: 0 }}>
                                                <div
                                                  className="d-flex align-items-center flex-wrap"
                                                  style={{ gap: "0.5rem", marginBottom: "1.25rem" }}
                                                >
                                                  <Button
                                                    type="button"
                                                    size="sm"
                                                    color={isDark ? undefined : "secondary"}
                                                    disabled={isBusy}
                                                    style={{
                                                      backgroundColor: isDark ? "#374151" : undefined,
                                                      borderColor: isDark ? "#4b5563" : undefined,
                                                      color: isDark ? "#f8f9fa" : undefined,
                                                      fontWeight: 600,
                                                    }}
                                                    onClick={() => {
                                                      const currentImage = settings.image;

                                                      if (currentImage) {
                                                        openDirectoryImageEditor(dirUuid, null, currentImage);
                                                      } else {
                                                        document.getElementById(`dir-image-${dirUuid}`)?.click();
                                                      }
                                                    }}
                                                  >
                                                    {hasImage ? "Edit Image" : "Choose Image"}
                                                  </Button>

                                                  {hasImage && (
                                                    <Button
                                                      type="button"
                                                      size="sm"
                                                      color="danger"
                                                      disabled={isBusy}
                                                      style={{ fontWeight: 600 }}
                                                      onClick={() => {
                                                        setDirectoryDisplaySettings((prev) => ({
                                                          ...prev,
                                                          [dirUuid]: {
                                                            ...(prev[dirUuid] || {}),
                                                            image: null,
                                                            imageName: "",
                                                            selectedColor: "",
                                                            removeImage: true,
                                                          },
                                                        }));
                                                      }}
                                                    >
                                                      Remove Image
                                                    </Button>
                                                  )}
                                                </div>

                                                <Input
                                                  id={`dir-image-${dirUuid}`}
                                                  type="file"
                                                  accept="image/png,image/jpeg"
                                                  disabled={isBusy}
                                                  onChange={async (e) => {
                                                    const file = e.target.files?.[0];
                                                    if (!file) return;

                                                    if (!file.type.startsWith("image/")) {
                                                      setQuickSetupStatus({
                                                        show: true,
                                                        success: false,
                                                        message: "Please select a valid image file.",
                                                      });
                                                      e.target.value = "";
                                                      return;
                                                    }

                                                    await openDirectoryImageEditor(dirUuid, file);
                                                    e.target.value = "";
                                                  }}
                                                  style={{ display: "none" }}
                                                />

                                                <div
                                                  style={{
                                                    color: isDark ? "#d1d5db" : "#374151",
                                                    fontSize: "0.78rem",
                                                    fontWeight: 700,
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.04em",
                                                    marginBottom: "0.45rem",
                                                  }}
                                                >
                                                  Use solid color
                                                </div>

                                                <div
                                                  className="d-flex align-items-center flex-wrap"
                                                  style={{ gap: "0.45rem" }}
                                                >
                                                  {DIRECTORY_IMAGE_COLOR_OPTIONS.map((option) => {
                                                    const isSelected = settings.selectedColor === option.hex;

                                                    return (
                                                      <button
                                                        key={`${dirUuid}-${option.hex}`}
                                                        type="button"
                                                        disabled={isBusy}
                                                        onClick={() => handleDirectoryColorSelect(dirUuid, option)}
                                                        title={option.label}
                                                        aria-label={`Use ${option.label} as directory image`}
                                                        style={{
                                                          width: "26px",
                                                          height: "26px",
                                                          borderRadius: "999px",
                                                          padding: 0,
                                                          backgroundColor: option.hex,
                                                          border:
                                                            option.hex === "#ffffff"
                                                              ? "1px solid #d1d5db"
                                                              : "1px solid rgba(0,0,0,0.25)",
                                                          outline: isSelected
                                                            ? `3px solid ${isDark ? "#93c5fd" : "#5e72e4"}`
                                                            : "none",
                                                          outlineOffset: "2px",
                                                          boxShadow: isSelected
                                                            ? "0 0 0 1px rgba(0,0,0,0.08)"
                                                            : "none",
                                                          cursor: isBusy ? "not-allowed" : "pointer",
                                                          opacity: isBusy ? 0.55 : 1,
                                                        }}
                                                      />
                                                    );
                                                  })}
                                                </div>

                                                {settings.imageName && (
                                                  <div
                                                    style={{
                                                      color: isDark ? "#9ca3af" : "#6b7280",
                                                      fontSize: "0.78rem",
                                                      marginTop: "0.55rem",
                                                      whiteSpace: "nowrap",
                                                      overflow: "hidden",
                                                      textOverflow: "ellipsis",
                                                    }}
                                                    title={settings.imageName}
                                                  >
                                                    {settings.imageName}
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>

                                    {/*                                     <small
                                      className="form-text mt-3"
                                      style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
                                    >
                                      Directory images and priorities are applied during the next enterphone sync.
                                    </small> */}
                                  </CardBody>
                                </Card>
                              </>
                            )}
                          </CardBody>
                        )}
                      </Card>
                    </div>
                  )}

                  {/* 2N IP Verso Display Settings */}
                  {editing && deviceData.device_model === "2N IP Verso" && (
                    <div className="pl-lg-4 mt-4">
                      <Card
                        className="shadow-sm"
                        style={{
                          border: isDark ? "1px solid #3a3a3a" : "1px solid #dcdfe3",
                          boxShadow: "none",
                        }}
                      >
                        <CardHeader
                          onClick={() => setDisplaySettingsOpen((prev) => !prev)}
                          style={{
                            ...collapsibleHeaderStyle,
                            borderBottom: displaySettingsOpen
                              ? isDark ? "1px solid #374151" : "1px solid #dcdfe3"
                              : "none",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h4
                                className="mb-1"
                                style={{
                                  color: isDark ? "#f8f9fa" : "#111",
                                  fontWeight: 600,
                                  fontSize: "1.35rem",
                                }}
                              >
                                Display Settings
                              </h4>
                              <div
                                style={{
                                  color: isDark ? "#9ca3af" : "#6c757d",
                                  fontSize: "0.95rem",
                                }}
                              >
                                Phonebook display, code entry, keypad mode, icon labels, & directory priorities
                              </div>
                            </div>

                            {renderCollapseIcon(displaySettingsOpen)}
                          </div>
                        </CardHeader>

                        {displaySettingsOpen && (
                          <CardBody
                            style={{
                              backgroundColor: isDark ? "#111827" : "#ffffff",
                              paddingTop: "1.25rem",
                            }}
                          >
                            {quickSetupStatus.show && (
                              <Alert
                                color={quickSetupStatus.success ? "success" : "danger"}
                                isOpen={quickSetupStatus.show}
                                toggle={() =>
                                  setQuickSetupStatus((prev) => ({ ...prev, show: false }))
                                }
                                className="mb-4"
                              >
                                {quickSetupStatus.message}
                              </Alert>
                            )}

                            <h6 className="heading-small mb-3" style={sectionHeadingStyle}>
                              Screen Options
                            </h6>

                            <Card
                              className="mb-4"
                              style={{
                                backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
                                border: isDark ? "1px solid #374151" : "1px solid #e9ecef",
                                boxShadow: "none",
                                opacity: versoDisplaySettingsLoading ? 0.7 : 1,
                              }}
                            >
                              <CardBody>
                                {[
                                  {
                                    id: "verso-phonebook-displayed-toggle",
                                    label: "Phonebook Displayed",
                                    description: "Controls whether the phonebook is shown on the device display.",
                                    field: "phonebookDisplayed",
                                  },
                                  {
                                    id: "verso-code-entry-button-toggle",
                                    label: "Code Entry Button",
                                    description: "Shows or hides the code entry button on the device display.",
                                    field: "codeEntryButton",
                                  },
                                  {
                                    id: "verso-prefer-icons-to-text-toggle",
                                    label: "Prefer Icons to Text",
                                    description: "Uses icons instead of text labels where the Verso display supports it.",
                                    field: "preferIconsToText",
                                  },
                                ].map((item) => (
                                  <div
                                    key={item.id}
                                    className="d-flex justify-content-between align-items-start"
                                    style={{
                                      padding: "0.5rem 0",
                                      gap: "1rem",
                                    }}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <div
                                        className="d-flex align-items-center"
                                        style={{
                                          color: isDark ? "#f8f9fa" : "#111",
                                          fontWeight: 600,
                                          marginBottom: "0.2rem",
                                          gap: "0.5rem",
                                        }}
                                      >
                                        <span>{item.label}</span>
                                        {versoDisplaySettingsLoading && (
                                          <small style={{ color: isDark ? "#9ca3af" : "#6c757d", fontWeight: 500 }}>
                                            Loading...
                                          </small>
                                        )}
                                      </div>

                                      <small className="form-text mt-0" style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                                        {item.description}
                                      </small>
                                    </div>

                                    <div className="custom-control custom-switch ml-3" style={{ paddingTop: "0.15rem" }}>
                                      <input
                                        type="checkbox"
                                        className="custom-control-input"
                                        id={item.id}
                                        checked={!!versoDisplaySettings[item.field]}
                                        onChange={handleVersoDisplayToggle(item.field)}
                                        disabled={isBusy || versoDisplaySettingsLoading}
                                        style={{
                                          cursor: (isBusy || versoDisplaySettingsLoading) ? "not-allowed" : "pointer",
                                        }}
                                      />

                                      <label
                                        className="custom-control-label"
                                        htmlFor={item.id}
                                        style={{
                                          color: isDark ? "#f8f9fa" : "#111",
                                          cursor: (isBusy || versoDisplaySettingsLoading) ? "not-allowed" : "pointer",
                                          minWidth: "32px",
                                        }}
                                      >
                                        {versoDisplaySettings[item.field] ? "On" : "Off"}
                                      </label>
                                    </div>
                                  </div>
                                ))}

                                <hr style={{ borderColor: isDark ? "#374151" : "#e9ecef", margin: "0.75rem 0" }} />

                                <div
                                  style={{
                                    padding: "0.25rem 0 0.5rem 0",
                                  }}
                                >
                                  <div className="d-flex align-items-center" style={{ gap: "0.5rem", marginBottom: "0.35rem" }}>
                                    <div style={{ color: isDark ? "#f8f9fa" : "#111", fontWeight: 600 }}>
                                      Code Entry Keypad Mode
                                    </div>

                                    {versoDisplaySettingsLoading && (
                                      <small style={{ color: isDark ? "#9ca3af" : "#6c757d", fontWeight: 500 }}>
                                        Loading...
                                      </small>
                                    )}
                                  </div>

                                  <small className="form-text mt-0 mb-2" style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                                    Normal keeps the keypad ordered. Scrambled randomizes the keypad layout.
                                  </small>

                                  <Input
                                    id="input-verso-entry-keypad-mode"
                                    type="select"
                                    value={versoDisplaySettings.entryKeypadMode}
                                    onChange={handleVersoKeypadModeChange}
                                    disabled={isBusy || versoDisplaySettingsLoading}
                                    style={{
                                      cursor: (isBusy || versoDisplaySettingsLoading) ? "not-allowed" : "pointer",
                                      backgroundColor: isDark ? "#2a2a2a" : "#fff",
                                      color: isDark ? "#f8f9fa" : "#111",
                                      borderColor: isDark ? "#3a3a3a" : undefined,
                                    }}
                                  >
                                    <option value="NORMAL">Normal</option>
                                    <option value="SCRAMBLED">Scrambled</option>
                                  </Input>
                                </div>
                              </CardBody>
                            </Card>

                            {(deviceData.dir_uuids || []).length > 0 && (
                              <>
                                <h6 className="heading-small mb-3" style={sectionHeadingStyle}>
                                  Directory Display Priorities
                                </h6>

                                <Card
                                  style={{
                                    backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
                                    border: isDark ? "1px solid #374151" : "1px solid #e9ecef",
                                    boxShadow: "none",
                                  }}
                                >
                                  <CardBody>
                                    {(deviceData.dir_uuids || []).map((dirUuid) => {
                                      const dir = directories.find((d) => d.dir_uuid === dirUuid);
                                      if (!dir) return null;

                                      return (
                                        <Row key={dirUuid} className="align-items-center mb-3" style={{ gap: "1rem" }}>
                                          <Col lg="auto" style={{ flex: "0 0 auto", minWidth: "220px", maxWidth: "320px" }}>
                                            <div style={{ color: isDark ? "#f8f9fa" : "#111", fontWeight: 600 }}>
                                              {dir.dir_name}
                                            </div>
                                          </Col>

                                          <Col lg="auto">
                                            <Input
                                              type="select"
                                              title="Priority controls the directory order on the device. Priority 1 appears at the top of the list. No Priority appears at the bottom."
                                              value={directoryDisplaySettings[dirUuid]?.priority ?? ""}
                                              disabled={isBusy}
                                              onChange={(e) => {
                                                const value = e.target.value;

                                                setDirectoryDisplaySettings((prev) => ({
                                                  ...prev,
                                                  [dirUuid]: {
                                                    ...(prev[dirUuid] || {}),
                                                    priority: value,
                                                  },
                                                }));
                                              }}
                                              style={{
                                                cursor: isBusy ? "not-allowed" : "pointer",
                                                backgroundColor: isDark ? "#2a2a2a" : "#fff",
                                                color: isDark ? "#f8f9fa" : "#111",
                                                borderColor: isDark ? "#3a3a3a" : undefined,
                                                width: "165px",
                                                minWidth: "165px",
                                                minHeight: "42px",
                                                height: "42px",
                                                fontSize: "0.9rem",
                                                lineHeight: "1.4",
                                                paddingTop: "0.5rem",
                                                paddingBottom: "0.5rem",
                                                paddingLeft: "0.75rem",
                                                paddingRight: "2rem",
                                              }}
                                            >
                                              <option value="">No Priority</option>
                                              <option value="1">★ 1</option>
                                              <option value="2">★ 2</option>
                                              <option value="3">★ 3</option>
                                              <option value="4">★ 4</option>
                                              <option value="5">★ 5</option>
                                              <option value="6">★ 6</option>
                                              <option value="7">★ 7</option>
                                            </Input>
                                          </Col>
                                        </Row>
                                      );
                                    })}

                                    <small className="form-text" style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                                      Directory priorities are applied during the next enterphone sync.
                                    </small>
                                  </CardBody>
                                </Card>
                              </>
                            )}
                          </CardBody>
                        )}
                      </Card>
                    </div>
                  )}

                  {editing && supportsCurrentDeviceApi && (
                    <div className="pl-lg-4">
                      <Card
                        className="shadow-sm"
                        style={{
                          border: isDark ? "1px solid #3a3a3a" : "1px solid #dcdfe3",
                          boxShadow: "none",
                        }}
                      >
                        <CardHeader
                          onClick={() => setActivityOpen((prev) => !prev)}
                          style={{
                            ...collapsibleHeaderStyle,
                            borderBottom: activityOpen
                              ? isDark ? "1px solid #374151" : "1px solid #dcdfe3"
                              : "none",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h4
                                className="mb-1"
                                style={{
                                  color: isDark ? "#f8f9fa" : "#111",
                                  fontWeight: 600,
                                  fontSize: "1.35rem",
                                }}
                              >
                                Call Logs & Events
                              </h4>
                              <div
                                style={{
                                  color: isDark ? "#9ca3af" : "#6c757d",
                                  fontSize: "0.95rem",
                                }}
                              >
                                Call records and event history from the intercom
                              </div>
                            </div>

                            {renderCollapseIcon(activityOpen)}
                          </div>
                        </CardHeader>

                        {activityOpen && (
                          <CardBody style={{ backgroundColor: isDark ? "#111827" : "#ffffff" }}>

                            <Row>
                              <Col lg="12" className="mb-4">
                                <Card
                                  className="shadow-sm"
                                  style={{
                                    border: isDark ? "1px solid #3a3a3a" : "1px solid #dcdfe3",
                                    boxShadow: "none",
                                  }}
                                >
                                  <CardHeader
                                    onClick={() => setCallLogsOpen((prev) => !prev)}
                                    style={{
                                      backgroundColor: isDark ? "#1f2937" : "#ffffff",
                                      borderBottom: callLogsOpen
                                        ? (isDark ? "1px solid #374151" : "1px solid #dcdfe3")
                                        : "none",
                                      paddingTop: "1rem",
                                      paddingBottom: "1rem",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div>
                                        <h4
                                          className="mb-1"
                                          style={{
                                            color: isDark ? "#f8f9fa" : "#111",
                                            fontWeight: 500,
                                            fontSize: "1.35rem",
                                            letterSpacing: "0.01em",
                                          }}
                                        >
                                          Call Logs
                                        </h4>
                                        <div
                                          style={{
                                            color: isDark ? "#9ca3af" : "#6c757d",
                                            fontSize: "0.95rem",
                                          }}
                                        >
                                          Call records from the intercom
                                        </div>
                                      </div>

                                      <div
                                        style={{
                                          color: isDark ? "#d1d5db" : "#495057",
                                          fontSize: "1.2rem",
                                          fontWeight: 700,
                                          userSelect: "none",
                                        }}
                                      >
                                        {callLogsOpen ? "−" : "+"}
                                      </div>
                                    </div>
                                  </CardHeader>

                                  {callLogsOpen && (
                                    <CardBody
                                      style={{
                                        backgroundColor: isDark ? "#111827" : "#ffffff",
                                        paddingTop: "1rem",
                                      }}
                                    >
                                      <div
                                        className="d-flex justify-content-between align-items-center flex-wrap mb-3"
                                        style={{ gap: "0.75rem" }}
                                      >
                                        <div style={{ minWidth: 180 }}>
                                          <Input
                                            type="select"
                                            value={callLogsRange}
                                            onChange={(e) => setCallLogsRange(e.target.value)}
                                            style={{
                                              backgroundColor: isDark ? "#2a2a2a" : "#fff",
                                              color: isDark ? "#f8f9fa" : "#111",
                                              borderColor: isDark ? "#3a3a3a" : "#ced4da",
                                              fontSize: "0.95rem",
                                              cursor: "pointer",
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <option value="1D">Last 1 Day</option>
                                            <option value="3D">Last 3 Days</option>
                                            <option value="7D">Last 7 Days</option>
                                            <option value="ALL">All</option>
                                          </Input>
                                        </div>

                                        <Button
                                          color="primary"
                                          size="sm"
                                          onClick={loadCallLogs}
                                          disabled={!device_uuid || callLogsLoading}
                                          style={{
                                            minWidth: "140px",
                                            fontWeight: 600,
                                            cursor: "pointer",
                                          }}
                                        >
                                          {callLogsLoading ? (
                                            <>
                                              <Spinner size="sm" className="mr-2" />
                                              Loading...
                                            </>
                                          ) : (
                                            <>
                                              <span className="btn-inner--icon mr-1">
                                                <i className="fas fa-sync" />
                                              </span>
                                              <span className="btn-inner--text">Refresh</span>
                                            </>
                                          )}
                                        </Button>
                                      </div>

                                      {callLogsError && (
                                        <Alert color="danger">{callLogsError}</Alert>
                                      )}

                                      {!callLogsLoading && filteredCallLogs.length === 0 && !callLogsError && (
                                        <div
                                          style={{
                                            color: isDark ? "#9ca3af" : "#6c757d",
                                            padding: "0.5rem 0",
                                          }}
                                        >
                                          No call logs found for the selected filters.
                                        </div>
                                      )}

                                      {filteredCallLogs.length > 0 && (
                                        <div className="table-responsive">
                                          <Table responsive className="mb-0" style={{ backgroundColor: "transparent" }}>
                                            <thead>
                                              <tr
                                                style={{
                                                  borderBottom: isDark ? "1px solid #374151" : "1px solid #bfc5cc",
                                                }}
                                              >
                                                <th
                                                  style={{
                                                    color: isDark ? "#f8f9fa" : "#111",
                                                    fontSize: "0.95rem",
                                                    fontWeight: 700,
                                                    paddingLeft: "1rem",
                                                    paddingRight: "1rem",
                                                    borderTop: "none",
                                                    width: "42%",
                                                  }}
                                                >
                                                  Name
                                                </th>
                                                <th
                                                  style={{
                                                    color: isDark ? "#f8f9fa" : "#111",
                                                    fontSize: "0.95rem",
                                                    fontWeight: 700,
                                                    paddingLeft: "1rem",
                                                    paddingRight: "1rem",
                                                    borderTop: "none",
                                                    width: "26%",
                                                  }}
                                                >
                                                  Date and Time
                                                </th>
                                                <th
                                                  style={{
                                                    color: isDark ? "#f8f9fa" : "#111",
                                                    fontSize: "0.95rem",
                                                    fontWeight: 700,
                                                    paddingLeft: "1rem",
                                                    paddingRight: "1rem",
                                                    borderTop: "none",
                                                    width: "18%",
                                                  }}
                                                >
                                                  Type
                                                </th>
                                                <th
                                                  style={{
                                                    color: isDark ? "#f8f9fa" : "#111",
                                                    fontSize: "0.95rem",
                                                    fontWeight: 700,
                                                    paddingLeft: "1rem",
                                                    paddingRight: "1rem",
                                                    borderTop: "none",
                                                    width: "14%",
                                                  }}
                                                >
                                                  Call Duration
                                                </th>
                                              </tr>
                                            </thead>

                                            <tbody>
                                              {filteredCallLogs.map((log, idx) => {
                                                const formatted = formatCallLogDate(log.date);
                                                const displayName = formatCallLogName(log);
                                                const displayType = getCallTypeDisplay(log.call_type);
                                                const displayDuration = formatCallLogDuration(log.duration);

                                                return (
                                                  <tr
                                                    key={`${log.id || "log"}-${idx}`}
                                                    style={{
                                                      borderBottom: isDark ? "1px solid #374151" : "1px solid #e6e9ed",
                                                    }}
                                                  >
                                                    <td
                                                      style={{
                                                        padding: "0.95rem 1rem",
                                                        verticalAlign: "middle",
                                                        color: isDark ? "#f8f9fa" : "#111",
                                                        borderTop: "none",
                                                      }}
                                                    >
                                                      <div className="d-flex align-items-center" style={{ gap: "0.85rem" }}>
                                                        <div
                                                          style={{
                                                            fontSize: "1.6rem",
                                                            lineHeight: 1,
                                                            color:
                                                              String(log.call_type || "").toLowerCase() === "outgoing"
                                                                ? "#66bb33"
                                                                : String(log.call_type || "").toLowerCase() === "incoming"
                                                                  ? "#2d9cdb"
                                                                  : "#f59e0b",
                                                            minWidth: "22px",
                                                          }}
                                                        >
                                                          {String(log.call_type || "").toLowerCase() === "outgoing"
                                                            ? "↗"
                                                            : String(log.call_type || "").toLowerCase() === "incoming"
                                                              ? "↙"
                                                              : "•"}
                                                        </div>

                                                        <div>
                                                          <div
                                                            style={{
                                                              fontSize: "1.05rem",
                                                              fontWeight: 500,
                                                              color: isDark ? "#f8f9fa" : "#111",
                                                            }}
                                                          >
                                                            {displayName}
                                                          </div>

                                                          {log.remote_party && log.remote_party !== displayName && (
                                                            <div
                                                              style={{
                                                                fontSize: "0.86rem",
                                                                color: isDark ? "#9ca3af" : "#6c757d",
                                                                marginTop: "0.1rem",
                                                              }}
                                                            >
                                                              {String(log.remote_party).replace(/^sip:/i, "")}
                                                            </div>
                                                          )}
                                                        </div>
                                                      </div>
                                                    </td>

                                                    <td
                                                      style={{
                                                        padding: "0.95rem 1rem",
                                                        verticalAlign: "middle",
                                                        borderTop: "none",
                                                      }}
                                                    >
                                                      <div
                                                        style={{
                                                          fontSize: "1rem",
                                                          color: isDark ? "#f8f9fa" : "#111",
                                                          fontWeight: 500,
                                                          lineHeight: 1.25,
                                                        }}
                                                      >
                                                        {formatted.date}
                                                      </div>
                                                      {formatted.time && (
                                                        <div
                                                          style={{
                                                            fontSize: "0.96rem",
                                                            color: isDark ? "#d1d5db" : "#343a40",
                                                            marginTop: "0.15rem",
                                                            lineHeight: 1.2,
                                                          }}
                                                        >
                                                          {formatted.time}
                                                        </div>
                                                      )}
                                                    </td>

                                                    <td
                                                      style={{
                                                        padding: "0.95rem 1rem",
                                                        verticalAlign: "middle",
                                                        borderTop: "none",
                                                      }}
                                                    >
                                                      <span
                                                        style={{
                                                          fontSize: "0.95rem",
                                                          color: isDark ? "#e5e7eb" : "#212529",
                                                        }}
                                                      >
                                                        {displayType}
                                                      </span>
                                                    </td>

                                                    <td
                                                      style={{
                                                        padding: "0.95rem 1rem",
                                                        verticalAlign: "middle",
                                                        borderTop: "none",
                                                        fontSize: "1rem",
                                                        color: isDark ? "#f8f9fa" : "#111",
                                                        whiteSpace: "nowrap",
                                                      }}
                                                    >
                                                      {displayDuration}
                                                    </td>
                                                  </tr>
                                                );
                                              })}
                                            </tbody>
                                          </Table>
                                        </div>
                                      )}
                                    </CardBody>
                                  )}
                                </Card>
                              </Col>

                              <Col lg="12">
                                <Card
                                  className="shadow-sm"
                                  style={{
                                    border: isDark ? "1px solid #3a3a3a" : "1px solid #dcdfe3",
                                    boxShadow: "none",
                                  }}
                                >
                                  <CardHeader
                                    onClick={() => setLiveEventsOpen((prev) => !prev)}
                                    style={{
                                      backgroundColor: isDark ? "#1f2937" : "#ffffff",
                                      borderBottom: liveEventsOpen
                                        ? (isDark ? "1px solid #374151" : "1px solid #dcdfe3")
                                        : "none",
                                      paddingTop: "1rem",
                                      paddingBottom: "1rem",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div>
                                        <h4
                                          className="mb-1"
                                          style={{
                                            color: isDark ? "#f8f9fa" : "#111",
                                            fontWeight: 500,
                                            fontSize: "1.35rem",
                                          }}
                                        >
                                          Events
                                        </h4>
                                        <div
                                          style={{
                                            color: isDark ? "#9ca3af" : "#6c757d",
                                            fontSize: "0.95rem",
                                          }}
                                        >
                                          Event history from the intercom
                                        </div>
                                      </div>

                                      <div
                                        style={{
                                          color: isDark ? "#d1d5db" : "#495057",
                                          fontSize: "1.2rem",
                                          fontWeight: 700,
                                          userSelect: "none",
                                        }}
                                      >
                                        {liveEventsOpen ? "−" : "+"}
                                      </div>
                                    </div>
                                  </CardHeader>

                                  {liveEventsOpen && (
                                    <CardBody
                                      style={{
                                        backgroundColor: isDark ? "#111827" : "#ffffff",
                                        paddingTop: "1rem",
                                      }}
                                    >
                                      <div
                                        className="d-flex justify-content-between align-items-center flex-wrap mb-3"
                                        style={{ gap: "0.75rem" }}
                                      >
                                        <div
                                          className="d-flex align-items-center"
                                          style={{ gap: "0.75rem", flexWrap: "wrap" }}
                                        >
                                          <div style={{ minWidth: 240 }}>
                                            <Input
                                              type="select"
                                              value={eventFilter}
                                              onChange={(e) => setEventFilter(e.target.value)}
                                              style={{
                                                backgroundColor: isDark ? "#2a2a2a" : "#fff",
                                                color: isDark ? "#f8f9fa" : "#111",
                                                borderColor: isDark ? "#3a3a3a" : "#ced4da",
                                                fontSize: "0.95rem",
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <option value="ALL">[Filter Events]</option>
                                              {availableEventTypes.map((type) => (
                                                <option key={type} value={type}>
                                                  {type}
                                                </option>
                                              ))}
                                            </Input>
                                          </div>

                                          <div style={{ minWidth: 180 }}>
                                            <Input
                                              type="select"
                                              value={eventsRange}
                                              onChange={(e) => setEventsRange(e.target.value)}
                                              style={{
                                                backgroundColor: isDark ? "#2a2a2a" : "#fff",
                                                color: isDark ? "#f8f9fa" : "#111",
                                                borderColor: isDark ? "#3a3a3a" : "#ced4da",
                                                fontSize: "0.95rem",
                                                cursor: "pointer",
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <option value="1D">Last 1 Day</option>
                                              <option value="3D">Last 3 Days</option>
                                              <option value="7D">Last 7 Days</option>
                                              <option value="ALL">All</option>
                                            </Input>
                                          </div>
                                        </div>

                                        <div className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
                                          <Button
                                            color="primary"
                                            size="sm"
                                            onClick={loadEvents}
                                            disabled={eventsLoading}
                                            style={{ minWidth: "120px" }}
                                          >
                                            {eventsLoading ? (
                                              <>
                                                <Spinner size="sm" className="mr-2" />
                                                Loading...
                                              </>
                                            ) : (
                                              <>
                                                <span className="btn-inner--icon mr-1">
                                                  <i className="fas fa-sync" />
                                                </span>
                                                <span className="btn-inner--text">Refresh</span>
                                              </>
                                            )}
                                          </Button>
                                        </div>
                                      </div>

                                      {eventsError && (
                                        <Alert color="danger">{eventsError}</Alert>
                                      )}

                                      {eventsLoading && filteredEvents.length === 0 && !eventsError && (
                                        <div
                                          style={{
                                            color: isDark ? "#9ca3af" : "#6c757d",
                                            padding: "0.5rem 0",
                                          }}
                                        >
                                          Loading events...
                                        </div>
                                      )}

                                      {!eventsLoading && filteredEvents.length === 0 && !eventsError && (
                                        <div
                                          style={{
                                            color: isDark ? "#9ca3af" : "#6c757d",
                                            padding: "0.5rem 0",
                                          }}
                                        >
                                          No events found for the selected filters.
                                        </div>
                                      )}

                                      {filteredEvents.length > 0 && (
                                        <div
                                          style={{
                                            border: isDark ? "1px solid #374151" : "1px solid #dcdfe3",
                                            maxHeight: "500px",
                                            overflowY: "auto",
                                            overflowX: "auto",
                                          }}
                                        >
                                          <Table
                                            responsive
                                            className="mb-0"
                                            style={{
                                              backgroundColor: isDark ? "#111827" : "#ffffff",
                                              minWidth: "900px",
                                            }}
                                          >
                                            <thead
                                              style={{
                                                position: "sticky",
                                                top: 0,
                                                zIndex: 2,
                                                backgroundColor: isDark ? "#1f2937" : "#f5f5f5",
                                              }}
                                            >
                                              <tr
                                                style={{
                                                  borderBottom: isDark ? "1px solid #374151" : "1px solid #cfd4da",
                                                }}
                                              >
                                                <th
                                                  style={{
                                                    width: "140px",
                                                    minWidth: "140px",
                                                    color: isDark ? "#f8f9fa" : "#111",
                                                    fontSize: "0.85rem",
                                                    fontWeight: 700,
                                                    borderTop: "none",
                                                    padding: "0.75rem 1rem",
                                                    backgroundColor: isDark ? "#1f2937" : "#f5f5f5",
                                                  }}
                                                >
                                                  TIME
                                                </th>
                                                <th
                                                  style={{
                                                    width: "220px",
                                                    minWidth: "220px",
                                                    color: isDark ? "#f8f9fa" : "#111",
                                                    fontSize: "0.85rem",
                                                    fontWeight: 700,
                                                    borderTop: "none",
                                                    padding: "0.75rem 1rem",
                                                    backgroundColor: isDark ? "#1f2937" : "#f5f5f5",
                                                  }}
                                                >
                                                  EVENT TYPE
                                                </th>
                                                <th
                                                  style={{
                                                    minWidth: "520px",
                                                    color: isDark ? "#f8f9fa" : "#111",
                                                    fontSize: "0.85rem",
                                                    fontWeight: 700,
                                                    borderTop: "none",
                                                    padding: "0.75rem 1rem",
                                                    backgroundColor: isDark ? "#1f2937" : "#f5f5f5",
                                                  }}
                                                >
                                                  DESCRIPTION
                                                </th>
                                              </tr>
                                            </thead>

                                            <tbody>
                                              {filteredEvents.map((evt, idx) => (
                                                <tr
                                                  key={`${evt.id || "evt"}-${idx}`}
                                                  style={{
                                                    borderBottom: isDark ? "1px solid #2f3b4d" : "1px solid #eceff3",
                                                  }}
                                                >
                                                  <td
                                                    style={{
                                                      borderTop: "none",
                                                      padding: "0.45rem 1rem",
                                                      verticalAlign: "top",
                                                      color: isDark ? "#e5e7eb" : "#212529",
                                                      fontSize: "0.95rem",
                                                      whiteSpace: "nowrap",
                                                    }}
                                                  >
                                                    {formatEventTime(evt)}
                                                  </td>

                                                  <td
                                                    style={{
                                                      borderTop: "none",
                                                      padding: "0.45rem 1rem",
                                                      verticalAlign: "top",
                                                      color: isDark ? "#f8f9fa" : "#111",
                                                      fontSize: "0.98rem",
                                                      fontWeight: 700,
                                                      whiteSpace: "nowrap",
                                                    }}
                                                  >
                                                    {formatEventType(evt)}
                                                  </td>

                                                  <td
                                                    style={{
                                                      borderTop: "none",
                                                      padding: "0.45rem 1rem",
                                                      verticalAlign: "top",
                                                      color: isDark ? "#f3f4f6" : "#111",
                                                      fontSize: "0.96rem",
                                                      lineHeight: 1.35,
                                                      whiteSpace: "nowrap",
                                                    }}
                                                  >
                                                    {formatEventDescription(evt)}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </Table>
                                        </div>
                                      )}
                                    </CardBody>
                                  )}
                                </Card>
                              </Col>
                            </Row>
                          </CardBody>
                        )}
                      </Card>
                    </div>
                  )}
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container >
      {isBusy && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: isDark ? "rgba(0,0,0,0.55)" : "rgba(255,255,255,0.7)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Card
            style={{
              padding: "1.5rem 2rem",
              borderRadius: "1rem",
              backgroundColor: isDark ? "#2a2a2a" : "#ffffff",
              color: isDark ? "#f8f9fa" : "#111827",
              border: isDark ? "1px solid #3a3a3a" : "1px solid #dee2e6",
              textAlign: "center",
              minWidth: 300,
            }}
          >
            <div className="spinner-border" role="status" />

            <div className="mt-3" style={{ fontWeight: 600 }}>
              {saving
                ? "Saving device…"
                : backgroundUploading
                  ? "Uploading background…"
                  : backgroundRemoving
                    ? "Removing background..."
                    : "Applying display settings…"}
            </div>

            {(backgroundUploading || backgroundRemoving || quickSetupBusy) ? (
              <>
                <div style={{ opacity: 0.8, fontSize: "0.9rem", marginTop: "4px" }}>
                  This may take up to <strong>15 seconds</strong>.
                </div>
                <div style={{ opacity: 0.7, fontSize: "0.85rem" }}>
                  Please do not close or refresh this page.
                </div>
              </>
            ) : (
              <div style={{ opacity: 0.7, fontSize: "0.85rem", marginTop: "4px" }}>
                Please wait...
              </div>
            )}
          </Card>
        </div>
      )
      }
      {imageEditorOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 3000,
            backgroundColor: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "850px",
              backgroundColor: isDark ? "#1f2937" : "#fff",
              color: isDark ? "#f8f9fa" : "#111",
              borderRadius: "0.75rem",
              overflow: "hidden",
              boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
            }}
          >
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                padding: "1rem 1.25rem",
                borderBottom: isDark ? "1px solid #374151" : "1px solid #dee2e6",
              }}
            >
              <h4 className="mb-0" style={{ color: isDark ? "#f8f9fa" : "#111" }}>
                Photo Editor
              </h4>

              <button
                type="button"
                onClick={closeDirectoryImageEditor}
                style={{
                  border: "none",
                  background: "transparent",
                  color: isDark ? "#f8f9fa" : "#111",
                  fontSize: "1.75rem",
                  lineHeight: 1,
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: "1.25rem" }}>
              {imageEditorError && (
                <Alert color="danger" className="mb-3">
                  {imageEditorError}
                </Alert>
              )}

              <div
                ref={imageEditorRef}
                style={{
                  width: "100%",
                  height: "420px",
                  borderRadius: "0.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  backgroundImage:
                    "linear-gradient(45deg, #bbb 25%, transparent 25%), linear-gradient(-45deg, #bbb 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #bbb 75%), linear-gradient(-45deg, transparent 75%, #bbb 75%)",
                  backgroundColor: "#eee",
                  backgroundSize: "20px 20px",
                  backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
                }}
              >
                <div
                  ref={imageEditorRef}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setIsDraggingImage(true);
                    setDragStart({
                      x: e.clientX,
                      y: e.clientY,
                      panX,
                      panY,
                    });
                  }}
                  onMouseMove={(e) => {
                    if (!isDraggingImage) return;

                    setPanX(dragStart.panX + (e.clientX - dragStart.x));
                    setPanY(dragStart.panY + (e.clientY - dragStart.y));
                  }}
                  onMouseUp={() => setIsDraggingImage(false)}
                  onMouseLeave={() => setIsDraggingImage(false)}
                  style={{
                    width: "320px",
                    height: "320px",
                    borderRadius: "50%",
                    overflow: "hidden",
                    position: "relative",
                    backgroundColor: "transparent",
                    border: "3px solid rgba(255,255,255,0.85)",
                    cursor: isDraggingImage ? "grabbing" : "grab",
                  }}
                >
                  {imageEditorSrc && (
                    <img
                      src={imageEditorSrc}
                      alt="Directory preview"
                      draggable={false}
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        maxWidth: "none",
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        transform: `translate(calc(-50% + ${panX}px), calc(-50% + ${panY}px)) scale(${zoom})`,
                        transformOrigin: "center",
                        userSelect: "none",
                        pointerEvents: "none",
                      }}
                    />
                  )}
                </div>
              </div>

              <div className="mt-4">
                <label style={{ fontWeight: 600 }}>Zoom</label>

                <Input
                  type="range"
                  min="0.1"
                  max="3"
                  step="0.05"
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  style={{ cursor: "pointer" }}
                />
              </div>

              <small style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                Move and zoom the image so it fits inside the circle. The final image will be compressed before saving.
              </small>
            </div>

            <div
              className="d-flex justify-content-end"
              style={{
                gap: "0.5rem",
                padding: "1rem 1.25rem",
                borderTop: isDark ? "1px solid #374151" : "1px solid #dee2e6",
              }}
            >
              <Button
                color="secondary"
                type="button"
                onClick={() => {
                  document.getElementById(`dir-image-${imageEditorDirUuid}`)?.click();
                }}
              >
                Choose Different Image
              </Button>

              <Button color="light" type="button" onClick={closeDirectoryImageEditor}>
                Cancel
              </Button>

              <Button
                color="primary"
                type="button"
                onClick={saveEditedDirectoryImage}
                disabled={imageEditorResultTooLarge || imageEditorResultBytes > MAX_DIRECTORY_IMAGE_BYTES}
              >
                OK
              </Button>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={tenantPickerOpen}
        toggle={closeTenantPicker}
        size="xl"
        centered
      >
        <ModalHeader
          toggle={closeTenantPicker}
          style={{
            backgroundColor: isDark ? "#1f2937" : "#ffffff",
            color: isDark ? "#f8f9fa" : "#111",
            borderBottom: isDark ? "1px solid #374151" : "1px solid #dee2e6",
          }}
        >
          Add User
          {tenantPickerButtonNumber ? ` - Button ${tenantPickerButtonNumber}` : ""}
        </ModalHeader>

        <ModalBody
          style={{
            backgroundColor: isDark ? "#111827" : "#ffffff",
            color: isDark ? "#f8f9fa" : "#111",
          }}
        >
          <div className="d-flex justify-content-end align-items-center mb-3">
            <label
              className="mb-0 mr-2"
              htmlFor="tenant-picker-search"
              style={{
                color: isDark ? "#d1d5db" : "#111",
                fontSize: "0.9rem",
              }}
            >
              Search
            </label>

            <Input
              id="tenant-picker-search"
              type="text"
              value={tenantPickerSearch}
              onChange={(e) => {
                setTenantPickerSearch(e.target.value);
                setTenantPickerPage(1);
              }}
              style={{
                width: "260px",
                backgroundColor: isDark ? "#2a2a2a" : "#fff",
                color: isDark ? "#f8f9fa" : "#111",
                borderColor: isDark ? "#3a3a3a" : undefined,
              }}
            />
          </div>

          <div style={{ overflowX: "auto" }}>
            <Table
              hover
              responsive
              className="mb-0"
              style={{
                color: isDark ? "#f8f9fa" : "#111",
                backgroundColor: isDark ? "#111827" : "#ffffff",
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      width: "56px",
                      textAlign: "center",
                      verticalAlign: "middle",
                      paddingLeft: "0.75rem",
                      paddingRight: "0.75rem",
                    }}
                  />
                  <th>Name</th>
                  <th>Virtual Number</th>
                  <th>Phone 1</th>
                </tr>
              </thead>

              <tbody>
                {tenantPickerPageTenants.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center py-4">
                      No tenants found.
                    </td>
                  </tr>
                ) : (
                  tenantPickerPageTenants.map((tenant) => {
                    const checked = isTenantPickerSelected(tenant.tenant_uuid);

                    return (
                      <tr
                        key={`tenant-picker-${tenant.tenant_uuid}`}
                        onClick={() => toggleTenantPickerTenant(tenant.tenant_uuid)}
                        style={{
                          cursor: "pointer",
                          backgroundColor: checked
                            ? isDark ? "#1f2937" : "#eef4ff"
                            : "transparent",
                        }}
                      >
                        <td
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: "56px",
                            textAlign: "center",
                            verticalAlign: "middle",
                            paddingLeft: "0.75rem",
                            paddingRight: "0.75rem",
                          }}
                        >
                          <Input
                            type="checkbox"
                            checked={checked}
                            disabled={isBusy}
                            onChange={() => toggleTenantPickerTenant(tenant.tenant_uuid)}
                            style={{
                              position: "static",
                              margin: 0,
                            }}
                          />
                        </td>
                        <td>{getTenantName(tenant)}</td>
                        <td>{getTenantVirtualNumber(tenant) || "—"}</td>
                        <td>{getTenantPhone1(tenant) || "—"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <div style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
              {tenantPickerFilteredTenants.length === 0
                ? "0 tenants"
                : `${(tenantPickerSafePage - 1) * TENANT_PICKER_PAGE_SIZE + 1} - ${Math.min(
                  tenantPickerSafePage * TENANT_PICKER_PAGE_SIZE,
                  tenantPickerFilteredTenants.length
                )} of ${tenantPickerFilteredTenants.length}`}
            </div>

            <div className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
              <Button
                size="sm"
                color="secondary"
                type="button"
                disabled={tenantPickerSafePage <= 1}
                style={
                  tenantPickerSafePage <= 1
                    ? tenantPickerSecondaryButtonDisabledStyle
                    : tenantPickerSecondaryButtonStyle
                }
                onClick={() =>
                  setTenantPickerPage((prev) => Math.max(1, prev - 1))
                }
              >
                Prev
              </Button>

              <span style={{ color: isDark ? "#d1d5db" : "#495057" }}>
                Page {tenantPickerSafePage} of {tenantPickerTotalPages}
              </span>

              <Button
                size="sm"
                color="secondary"
                type="button"
                disabled={tenantPickerSafePage >= tenantPickerTotalPages}
                style={
                  tenantPickerSafePage >= tenantPickerTotalPages
                    ? tenantPickerSecondaryButtonDisabledStyle
                    : tenantPickerSecondaryButtonStyle
                }
                onClick={() =>
                  setTenantPickerPage((prev) =>
                    Math.min(tenantPickerTotalPages, prev + 1)
                  )
                }
              >
                Next
              </Button>
            </div>
          </div>
        </ModalBody>

        <ModalFooter
          style={{
            backgroundColor: isDark ? "#111827" : "#ffffff",
            borderTop: isDark ? "1px solid #374151" : "1px solid #dee2e6",
          }}
        >
          <div
            className="mr-auto"
            style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
          >
            {tenantPickerSelected.length} selected
          </div>

          <Button
            color="primary"
            type="button"
            disabled={isBusy}
            onClick={saveTenantPickerSelection}
          >
            Add
          </Button>

          <Button
            color="secondary"
            type="button"
            style={tenantPickerSecondaryButtonStyle}
            onClick={closeTenantPicker}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default DeviceForm;
